import React, { useState, useEffect } from 'react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { Grid, Eye, Palette, Droplets, AlertCircle, CheckCircle, ChevronRight, Info, Glasses, Sun, Moon, EyeOff, Monitor, Baby, Thermometer, Calendar, X, Phone, Mail, MessageCircle, Maximize2, Minimize2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== MODAL POST-TEST (stile cookie banner) ====================
const TestCompletedModal = ({ isOpen, onClose, testResult, testName, onBookVisit, isLoggedIn, onSignupSuccess }) => {
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [resultsSent, setResultsSent] = useState(false);
  const [signupData, setSignupData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    privacy_accepted: false
  });
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupErrors, setSignupErrors] = useState({});

  if (!isOpen) return null;

  const isAltered = testResult?.altered || testResult?.needsAttention || false;
  
  // Funzione per aprire il chatbot
  const openChatbot = () => {
    const chatButton = document.querySelector('[data-testid="chat-widget-button"]') || 
                       document.querySelector('.chat-widget-button') ||
                       document.querySelector('button[aria-label*="chat"]');
    if (chatButton) {
      chatButton.click();
    } else {
      window.dispatchEvent(new CustomEvent('openChatWidget'));
    }
    onClose();
  };

  // Funzione per inviare il resoconto via email/SMS all'utente loggato
  const sendResultsToLoggedUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      await axios.post(`${API}/test-results/send-report`, {
        test_name: testName,
        result: testResult,
        user_id: user.id,
        email: user.email,
        phone: user.phone,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResultsSent(true);
      toast.success('Resoconto inviato! Controlla la tua email e gli SMS.');
    } catch (error) {
      console.error('Errore invio resoconto:', error);
      toast.error('Errore nell\'invio del resoconto. Riprova.');
    }
  };

  // Validazione form registrazione
  const validateSignup = () => {
    const errors = {};
    if (!signupData.first_name.trim()) errors.first_name = 'Nome obbligatorio';
    if (!signupData.last_name.trim()) errors.last_name = 'Cognome obbligatorio';
    if (!signupData.email.trim()) errors.email = 'Email obbligatoria';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email)) errors.email = 'Email non valida';
    if (!signupData.phone.trim()) errors.phone = 'Telefono obbligatorio';
    if (!signupData.password) errors.password = 'Password obbligatoria';
    else if (signupData.password.length < 8) errors.password = 'Min. 8 caratteri';
    if (signupData.password !== signupData.confirm_password) errors.confirm_password = 'Le password non coincidono';
    if (!signupData.privacy_accepted) errors.privacy = 'Devi accettare la privacy';
    return errors;
  };

  // Submit registrazione - L'utente diventa LEAD automaticamente
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    const errors = validateSignup();
    if (Object.keys(errors).length > 0) {
      setSignupErrors(errors);
      return;
    }

    setSignupLoading(true);
    try {
      let phone = signupData.phone.replace(/\s/g, '');
      if (!phone.startsWith('+')) phone = '+39' + phone;

      // Registrazione con source "test_visivi" per tracciare come LEAD
      const response = await axios.post(`${API}/auth/register-from-test`, {
        email: signupData.email,
        password: signupData.password,
        first_name: signupData.first_name,
        last_name: signupData.last_name,
        phone: phone,
        newsletter_subscribed: true,
        source: 'test_visivi_lead', // Marca come LEAD da test visivi
        test_result: {
          test_name: testName,
          result: testResult,
          altered: isAltered
        }
      });

      if (response.data?.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.access_token) localStorage.setItem('token', response.data.access_token);
        toast.success('Account creato! Il resoconto ti è stato inviato via email e SMS.');
        setResultsSent(true);
        onSignupSuccess?.();
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore durante la registrazione');
      }
    } finally {
      setSignupLoading(false);
    }
  };

  // Se non loggato e mostra form registrazione
  if (!isLoggedIn && showSignupForm) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#2A9FCB] to-[#1E88B5] p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Crea il tuo account</h3>
                  <p className="text-white/80 text-sm">Ricevi il resoconto gratuito</p>
                </div>
              </div>
              <button onClick={() => setShowSignupForm(false)} className="p-2 hover:bg-white/10 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSignupSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input
                  type="text"
                  placeholder="Nome *"
                  value={signupData.first_name}
                  onChange={(e) => setSignupData({...signupData, first_name: e.target.value})}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] ${signupErrors.first_name ? 'border-red-400' : 'border-gray-200'}`}
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Cognome *"
                  value={signupData.last_name}
                  onChange={(e) => setSignupData({...signupData, last_name: e.target.value})}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] ${signupErrors.last_name ? 'border-red-400' : 'border-gray-200'}`}
                />
              </div>
            </div>
            
            <input
              type="email"
              placeholder="Email *"
              value={signupData.email}
              onChange={(e) => setSignupData({...signupData, email: e.target.value})}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] ${signupErrors.email ? 'border-red-400' : 'border-gray-200'}`}
            />
            
            <input
              type="tel"
              placeholder="Telefono *"
              value={signupData.phone}
              onChange={(e) => setSignupData({...signupData, phone: e.target.value})}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] ${signupErrors.phone ? 'border-red-400' : 'border-gray-200'}`}
            />
            
            <div className="grid grid-cols-2 gap-3">
              <input
                type="password"
                placeholder="Password *"
                value={signupData.password}
                onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] ${signupErrors.password ? 'border-red-400' : 'border-gray-200'}`}
              />
              <input
                type="password"
                placeholder="Conferma password *"
                value={signupData.confirm_password}
                onChange={(e) => setSignupData({...signupData, confirm_password: e.target.value})}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#2A9FCB] ${signupErrors.confirm_password ? 'border-red-400' : 'border-gray-200'}`}
              />
            </div>
            
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={signupData.privacy_accepted}
                onChange={(e) => setSignupData({...signupData, privacy_accepted: e.target.checked})}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-[#2A9FCB] focus:ring-[#2A9FCB]"
              />
              <span className="text-sm text-gray-600">
                Accetto la <a href="/privacy" className="text-[#2A9FCB] underline">Privacy Policy</a> e acconsento a ricevere il resoconto del test via email
              </span>
            </label>
            {signupErrors.privacy && <p className="text-xs text-red-500">{signupErrors.privacy}</p>}
            
            <button
              type="submit"
              disabled={signupLoading}
              className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-[#2A9FCB] to-[#1E88B5] hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {signupLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Registrazione...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Crea account e ricevi resoconto
                </>
              )}
            </button>
            
            <p className="text-xs text-center text-gray-400">
              Iscrivendoti sarai automaticamente iscritto alla newsletter per ricevere consigli sulla salute visiva
            </p>
          </form>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div 
        className="w-full max-w-2xl bg-white rounded-t-2xl shadow-2xl transform transition-all animate-slide-up"
        style={{
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {isAltered ? (
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {isAltered ? 'Risultato da approfondire' : 'Test completato!'}
                </h3>
                <p className="text-sm text-gray-500">{testName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Se NON loggato: mostra solo CTA per registrarsi */}
          {!isLoggedIn && !resultsSent && (
            <>
              {/* Box nascosto risultati */}
              <div className="mb-6 p-5 rounded-xl bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-200 relative overflow-hidden">
                <div className="absolute inset-0 backdrop-blur-sm bg-white/70 flex items-center justify-center z-10">
                  <div className="text-center p-4">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-[#2A9FCB]/10 flex items-center justify-center">
                      <EyeOff className="w-8 h-8 text-[#2A9FCB]" />
                    </div>
                    <p className="font-semibold text-gray-800 mb-1">Risultati disponibili dopo la registrazione</p>
                    <p className="text-sm text-gray-500">Registrati gratuitamente per vedere i dettagli</p>
                  </div>
                </div>
                <div className="blur-sm pointer-events-none">
                  <p className="text-gray-400">Il tuo risultato del test...</p>
                  <p className="text-gray-400 text-sm mt-2">Dettagli e raccomandazioni personalizzate...</p>
                </div>
              </div>

              {/* CTA principale - Concludi Valutazione */}
              <div className="p-5 bg-gradient-to-r from-[#2A9FCB]/10 to-cyan-50 rounded-xl border border-[#2A9FCB]/20 mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#2A9FCB] flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-lg">Concludi la Valutazione</p>
                    <p className="text-sm text-gray-600">Registrati gratuitamente per ricevere il resoconto completo</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-600 space-y-2 mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Resoconto dettagliato via email
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Notifica SMS con i risultati
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Consigli personalizzati per la tua vista
                  </li>
                </ul>
                <button
                  onClick={() => setShowSignupForm(true)}
                  className="w-full py-4 rounded-xl font-bold text-white bg-gradient-to-r from-[#2A9FCB] to-[#1E88B5] hover:opacity-90 transition-all flex items-center justify-center gap-2 text-lg"
                  data-testid="conclude-evaluation-btn"
                >
                  <CheckCircle className="w-5 h-5" />
                  Concludi Valutazione e Ricevi Risultati
                </button>
              </div>
            </>
          )}

          {/* Se LOGGATO o risultati già inviati: mostra tutto */}
          {(isLoggedIn || resultsSent) && (
            <>
              {/* Warning box con risultato */}
              <div className={`p-4 rounded-xl mb-6 ${isAltered ? 'bg-amber-50 border border-amber-200' : 'bg-sky-50 border border-sky-200'}`}>
                <div className="flex items-start gap-3">
                  <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isAltered ? 'text-amber-600' : 'text-sky-600'}`} />
                  <div>
                    <p className={`font-medium ${isAltered ? 'text-amber-800' : 'text-sky-800'}`}>
                      {isAltered 
                        ? '⚠️ Il tuo risultato suggerisce la necessità di un controllo specialistico.'
                        : '✅ Il tuo test non ha evidenziato anomalie significative.'}
                    </p>
                    <p className={`text-sm mt-1 ${isAltered ? 'text-amber-700' : 'text-sky-700'}`}>
                      Il test di autovalutazione <strong>non sostituisce una visita oculistica</strong>. 
                      È importante effettuare controlli periodici per prevenire e diagnosticare precocemente 
                      eventuali patologie oculistiche.
                    </p>
                  </div>
                </div>
              </div>

              {/* Dettagli del risultato (solo per loggati) */}
              {testResult?.details && (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600">{testResult.details}</p>
                </div>
              )}

              {/* CTA per inviare resoconto (se loggato e non già inviato) */}
              {isLoggedIn && !resultsSent && (
                <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Ricevi il resoconto completo</p>
                      <p className="text-sm text-gray-600">Ti invieremo i risultati via email e SMS</p>
                    </div>
                  </div>
                  <button
                    onClick={sendResultsToLoggedUser}
                    className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 transition-all flex items-center justify-center gap-2"
                    data-testid="send-results-btn"
                  >
                    <Mail className="w-5 h-5" />
                    Invia Resoconto via Email e SMS
                  </button>
                </div>
              )}

              {/* Conferma invio */}
              {resultsSent && (
                <div className="mb-4 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-800">Resoconto inviato!</p>
                      <p className="text-sm text-green-700">Controlla la tua email e gli SMS per i dettagli completi.</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={onBookVisit}
              className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
              style={{background: isAltered ? '#F59E0B' : '#2A9FCB'}}
            >
              <Calendar className="w-5 h-5" />
              {isAltered ? 'Prenota subito una visita di controllo' : 'Prenota una visita oculistica'}
            </button>
            
            <div className="flex gap-3">
              <button
                onClick={openChatbot}
                className="flex-1 py-3 rounded-xl font-medium border-2 text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
                style={{background: '#2A9FCB', borderColor: '#2A9FCB'}}
              >
                <MessageCircle className="w-4 h-4" />
                Chatta con l'Assistente
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl font-medium border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all"
              >
                Chiudi
              </button>
            </div>
          </div>

          {/* Footer note */}
          <p className="text-xs text-center text-gray-400 mt-4">
            PRIME VISION - Centro di Eccellenza Oftalmologica
          </p>
        </div>
      </div>
      
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

// Lead Generation Form Component per chi non è loggato - REGISTRAZIONE UTENTE COMPLETA
const LeadGenerationForm = ({ onSuccess, onSkip }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    fiscal_code: '',
    privacy_accepted: false,
    newsletter_accepted: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'Nome obbligatorio';
    if (!formData.last_name.trim()) newErrors.last_name = 'Cognome obbligatorio';
    if (!formData.email.trim()) newErrors.email = 'Email obbligatoria';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email non valida';
    if (!formData.phone.trim()) newErrors.phone = 'Telefono obbligatorio';
    if (!formData.password) newErrors.password = 'Password obbligatoria';
    else if (formData.password.length < 8) newErrors.password = 'La password deve avere almeno 8 caratteri';
    if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'Le password non coincidono';
    if (!formData.privacy_accepted) newErrors.privacy = 'Devi accettare la privacy policy';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Normalizza il telefono
      let phone = formData.phone.replace(/\s/g, '');
      if (!phone.startsWith('+')) {
        phone = '+39' + phone;
      }

      // Registra l'utente come paziente (senza OTP per questa sezione)
      const response = await axios.post(`${API}/auth/register-from-test`, {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: phone,
        fiscal_code: formData.fiscal_code || null,
        newsletter_subscribed: formData.newsletter_accepted,
        source: 'test_page'
      });

      if (response.data && response.data.user) {
        toast.success('Registrazione completata! Ora puoi fare i test.');
        
        // Salva l'utente in localStorage
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        
        onSuccess();
      }
    } catch (error) {
      console.error('Errore registrazione:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore durante la registrazione. Riprova.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
          <Eye className="w-8 h-8 text-sky-600" />
        </div>
        <h3 className="text-2xl font-bold text-[#2A9FCB]">Registrati per i Test</h3>
        <p className="text-gray-500 mt-2">Crea il tuo account per salvare i risultati e ricevere consigli personalizzati</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500 ${errors.first_name ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Mario"
            />
            {errors.first_name && <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500 ${errors.last_name ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Rossi"
            />
            {errors.last_name && <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="mario.rossi@email.com"
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Telefono *</label>
          <div className="flex">
            <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm">
              +39
            </span>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className={`w-full px-4 py-2 border rounded-r-lg focus:ring-2 focus:ring-sky-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="333 1234567"
            />
          </div>
          {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500 pr-10 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Minimo 8 caratteri"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Conferma Password *</label>
          <input
            type={showPassword ? "text" : "password"}
            value={formData.confirm_password}
            onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500 ${errors.confirm_password ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Ripeti la password"
          />
          {errors.confirm_password && <p className="text-xs text-red-500 mt-1">{errors.confirm_password}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale (opzionale)</label>
          <input
            type="text"
            value={formData.fiscal_code}
            onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
            placeholder="RSSMRA80A01H501Z"
            maxLength={16}
          />
        </div>

        <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="privacy"
            checked={formData.privacy_accepted}
            onChange={(e) => setFormData({
              ...formData, 
              privacy_accepted: e.target.checked,
              newsletter_accepted: e.target.checked // Newsletter automatica con privacy
            })}
            className="mt-1 w-4 h-4"
          />
          <label htmlFor="privacy" className="text-sm text-gray-600 leading-relaxed">
            Accetto la <a href="/privacy" className="text-sky-600 underline">Privacy Policy</a> e acconsento 
            al trattamento dei miei dati personali per ricevere aggiornamenti, consigli sulla salute degli 
            occhi e comunicazioni dal centro PRIME VISION via email. *
          </label>
        </div>
        {errors.privacy && <p className="text-xs text-red-500">{errors.privacy}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl font-semibold text-white disabled:opacity-50 transition-all"
          style={{background: '#2A9FCB'}}
        >
          {loading ? 'Registrazione in corso...' : 'Registrati e Inizia i Test'}
        </button>
      </form>

      <div className="mt-4 text-center">
        <p className="text-sm text-gray-500 mb-2">Hai già un account?</p>
        <a href="/auth" className="text-sky-600 font-medium hover:underline">Accedi qui</a>
      </div>
    </div>
  );
};

// ==================== NUOVO: Test Acuità Visiva Adulti e Bambini (E di Albini) ====================
// Dimensioni delle E calcolate per 3 metri di distanza - DECIMI
// 4 E per ogni livello di acuità (decimo)
const ALBINI_E_CHILDREN = [
  { line: 1, decimi: '1/10', size: 180, eCount: 4 },
  { line: 2, decimi: '2/10', size: 90, eCount: 4 },
  { line: 3, decimi: '3/10', size: 63, eCount: 4 },
  { line: 4, decimi: '4/10', size: 45, eCount: 4 },
  { line: 5, decimi: '5/10', size: 36, eCount: 4 },
  { line: 6, decimi: '6/10', size: 27, eCount: 4 },
  { line: 7, decimi: '7/10', size: 22, eCount: 4 },
  { line: 8, decimi: '8/10', size: 19, eCount: 4 },
  { line: 9, decimi: '9/10', size: 17, eCount: 4 },
  { line: 10, decimi: '10/10', size: 15, eCount: 4 },
];

// Orientamenti possibili delle E
const E_ORIENTATIONS = ['up', 'down', 'left', 'right'];

// ==================== NUOVO: Questionario SPEED per Occhio Secco Evaporativo (MGD) ====================
const SPEED_QUESTIONS = [
  { 
    id: 'frequency',
    title: 'FREQUENZA dei sintomi',
    description: 'Con quale frequenza hai sperimentato questi sintomi nelle ultime 2 settimane?',
    symptoms: [
      { id: 'dryness', label: 'Secchezza, sensazione di sabbia o prurito' },
      { id: 'soreness', label: 'Dolore o irritazione' },
      { id: 'burning', label: 'Bruciore o lacrimazione' },
      { id: 'fatigue', label: 'Affaticamento oculare' },
    ],
    options: [
      { value: 0, label: 'Mai' },
      { value: 1, label: 'Qualche volta' },
      { value: 2, label: 'Spesso' },
      { value: 3, label: 'Costantemente' },
    ]
  },
  {
    id: 'severity',
    title: 'GRAVITÀ dei sintomi',
    description: 'Quanto sono stati gravi questi sintomi nelle ultime 2 settimane?',
    symptoms: [
      { id: 'dryness_sev', label: 'Secchezza, sensazione di sabbia o prurito' },
      { id: 'soreness_sev', label: 'Dolore o irritazione' },
      { id: 'burning_sev', label: 'Bruciore o lacrimazione' },
      { id: 'fatigue_sev', label: 'Affaticamento oculare' },
    ],
    options: [
      { value: 0, label: 'Assente' },
      { value: 1, label: 'Lieve' },
      { value: 2, label: 'Moderato' },
      { value: 3, label: 'Grave' },
    ]
  }
];

// Domande aggiuntive specifiche per MGD
const MGD_SPECIFIC_QUESTIONS = [
  { id: 'morning_symptoms', question: 'I sintomi sono peggiori al risveglio mattutino?', info: 'Tipico dell\'occhio secco evaporativo/MGD' },
  { id: 'screen_time', question: 'I sintomi peggiorano dopo l\'uso prolungato di schermi (PC, smartphone)?', info: 'L\'ammiccamento ridotto peggiora la MGD' },
  { id: 'air_conditioning', question: 'I sintomi peggiorano in ambienti con aria condizionata o riscaldamento?', info: 'L\'aria secca accelera l\'evaporazione lacrimale' },
  { id: 'eyelid_issues', question: 'Hai notato crosticine, arrossamento o gonfiore ai bordi delle palpebre?', info: 'Segno di blefarite spesso associata a MGD' },
  { id: 'oily_skin', question: 'Hai la pelle del viso tendenzialmente grassa o soffri di rosacea?', info: 'Condizioni associate alla disfunzione delle ghiandole di Meibomio' },
];

// Immagini per il test del daltonismo - scene comparative con tutti i colori
const COLORBLIND_TEST_IMAGES = [
  {
    id: 'fruits',
    name: 'Frutta e Verdura',
    description: 'Una scena con frutti e verdure di diversi colori',
    colors: ['rosso (mela, fragola)', 'verde (mela verde, kiwi)', 'giallo (banana, limone)', 'arancione (arancia)', 'viola (uva)'],
  },
  {
    id: 'traffic',
    name: 'Semaforo e Segnali',
    description: 'Semaforo con luci rosse, gialle e verdi',
    colors: ['rosso (stop)', 'giallo (attenzione)', 'verde (via libera)'],
  },
  {
    id: 'nature',
    name: 'Paesaggio Naturale',
    description: 'Tramonto con cielo, prato e fiori',
    colors: ['blu (cielo)', 'verde (prato)', 'rosso/arancio (tramonto)', 'giallo (sole)', 'viola (fiori)'],
  }
];

// Tavole di Ishihara - Immagini ufficiali (fonte: Challenge TB / USAID)
// 5 tavole selezionate per screening daltonismo
const ISHIHARA_PLATES = [
  { 
    id: 1, 
    number: '12', 
    altNumber: '12', 
    image: '/images/ishihara/plate1.png',
    description: 'Tavola 1 - Tutti vedono 12 (tavola dimostrativa)', 
    type: 'demo' 
  },
  { 
    id: 2, 
    number: '8', 
    altNumber: '3', 
    image: '/images/ishihara/plate2.png',
    description: 'Tavola 2 - Visione normale: 8 | Daltonici rosso-verde: 3', 
    type: 'transformation' 
  },
  { 
    id: 3, 
    number: '5', 
    altNumber: '6', 
    image: '/images/ishihara/plate3.png',
    description: 'Tavola 3 - Visione normale: 5 | Daltonici rosso-verde: 6', 
    type: 'transformation' 
  },
  { 
    id: 4, 
    number: '29', 
    altNumber: '70', 
    image: '/images/ishihara/plate4.png',
    description: 'Tavola 4 - Visione normale: 29 | Daltonici rosso-verde: 70', 
    type: 'transformation' 
  },
  { 
    id: 5, 
    number: '45', 
    altNumber: null, 
    image: '/images/ishihara/plate5.png',
    description: 'Tavola 5 - Visione normale: 45 | Daltonici rosso-verde: non vedono il numero', 
    type: 'vanishing' 
  },
];

// Lettere E di Albini per test rosso-verde (orientamenti diversi) - font sottile
const ALBINI_E_ROWS = [
  { size: 'text-5xl', letters: ['E'] }, // Una sola E grande al centro
];

// ==================== TEST SENSIBILITÀ AL CONTRASTO ====================
// 10 livelli di contrasto decrescente (dal più facile al più difficile)
const CONTRAST_LEVELS = [
  { level: 1, contrast: 100, label: 'Livello 1 - Contrasto massimo', letter: 'E', rotation: 0 },
  { level: 2, contrast: 80, label: 'Livello 2 - Alto contrasto', letter: 'E', rotation: 90 },
  { level: 3, contrast: 60, label: 'Livello 3 - Contrasto medio-alto', letter: 'E', rotation: 180 },
  { level: 4, contrast: 45, label: 'Livello 4 - Contrasto medio', letter: 'E', rotation: 270 },
  { level: 5, contrast: 35, label: 'Livello 5 - Contrasto medio-basso', letter: 'E', rotation: 0 },
  { level: 6, contrast: 25, label: 'Livello 6 - Basso contrasto', letter: 'E', rotation: 90 },
  { level: 7, contrast: 18, label: 'Livello 7 - Contrasto molto basso', letter: 'E', rotation: 180 },
  { level: 8, contrast: 12, label: 'Livello 8 - Contrasto scarso', letter: 'E', rotation: 270 },
  { level: 9, contrast: 7, label: 'Livello 9 - Contrasto minimo', letter: 'E', rotation: 0 },
  { level: 10, contrast: 4, label: 'Livello 10 - Soglia di percezione', letter: 'E', rotation: 90 },
];

// ==================== DEQ-5 (Dry Eye Questionnaire) in ITALIANO ====================
const DEQ5_QUESTIONS = [
  {
    id: 1,
    question: 'Con quale frequenza i tuoi occhi hanno una sensazione di secchezza?',
    type: 'frequency',
    options: [
      { value: 0, label: 'Mai' },
      { value: 1, label: 'Raramente' },
      { value: 2, label: 'Qualche volta' },
      { value: 3, label: 'Frequentemente' },
      { value: 4, label: 'Costantemente' },
    ]
  },
  {
    id: 2,
    question: 'Quando i tuoi occhi hanno una sensazione di secchezza, quanto è intensa questa sensazione alla fine della giornata?',
    type: 'intensity',
    options: [
      { value: 0, label: 'Mai provata' },
      { value: 1, label: 'Poco intensa' },
      { value: 2, label: 'Moderatamente intensa' },
      { value: 3, label: 'Intensa' },
      { value: 4, label: 'Molto intensa' },
    ]
  },
  {
    id: 3,
    question: 'Con quale frequenza i tuoi occhi hanno una sensazione acquosa o lacrimano?',
    type: 'frequency',
    options: [
      { value: 0, label: 'Mai' },
      { value: 1, label: 'Raramente' },
      { value: 2, label: 'Qualche volta' },
      { value: 3, label: 'Frequentemente' },
      { value: 4, label: 'Costantemente' },
    ]
  },
  {
    id: 4,
    question: 'Con quale frequenza i tuoi occhi hanno una sensazione di disagio (irritazione, bruciore, prurito)?',
    type: 'frequency',
    options: [
      { value: 0, label: 'Mai' },
      { value: 1, label: 'Raramente' },
      { value: 2, label: 'Qualche volta' },
      { value: 3, label: 'Frequentemente' },
      { value: 4, label: 'Costantemente' },
    ]
  },
  {
    id: 5,
    question: 'Quando i tuoi occhi hanno una sensazione di disagio, quanto è intensa questa sensazione alla fine della giornata?',
    type: 'intensity',
    options: [
      { value: 0, label: 'Mai provata' },
      { value: 1, label: 'Poco intensa' },
      { value: 2, label: 'Moderatamente intensa' },
      { value: 3, label: 'Intensa' },
      { value: 4, label: 'Molto intensa' },
    ]
  },
];

// Domande OSDI-6 per occhio secco (versione completa in italiano)
const OSDI_QUESTIONS = [
  { id: 1, question: 'Sensibilità alla luce?', category: 'symptoms' },
  { id: 2, question: 'Sensazione di sabbia/corpo estraneo negli occhi?', category: 'symptoms' },
  { id: 3, question: 'Dolore o bruciore agli occhi?', category: 'symptoms' },
  { id: 4, question: 'Visione offuscata?', category: 'symptoms' },
  { id: 5, question: 'Difficoltà a leggere?', category: 'activities' },
  { id: 6, question: 'Difficoltà nell\'uso del computer?', category: 'activities' }
];

const OSDI_OPTIONS = [
  { value: 0, label: 'Mai' },
  { value: 1, label: 'Qualche volta' },
  { value: 2, label: 'Metà del tempo' },
  { value: 3, label: 'Quasi sempre' },
  { value: 4, label: 'Sempre' }
];

// Simulazioni ALTRE patologie (daltonismo rimosso - ha sezione dedicata)
const VISION_SIMULATIONS = [
  { 
    id: 'myopia', 
    name: 'Miopia', 
    icon: '👓',
    description: 'Come vede un miope: gli oggetti lontani appaiono sfocati',
    filter: 'blur(4px)',
    explanation: 'L\'occhio miope è troppo lungo: i raggi luminosi convergono davanti alla retina invece che su di essa.'
  },
  { 
    id: 'astigmatism_night', 
    name: 'Astigmatismo (notte)', 
    icon: '🌙',
    description: 'Come vede un astigmatico di notte: luci allungate e sdoppiate',
    filter: 'blur(2px)',
    explanation: 'La cornea non è perfettamente sferica, causando immagini distorte soprattutto con le luci notturne.',
    isNight: true
  },
  { 
    id: 'maculopathy', 
    name: 'Maculopatia', 
    icon: '⚫',
    description: 'Come vede chi ha una maculopatia: macchia centrale scura',
    filter: 'none',
    hasCentralSpot: true,
    explanation: 'La macula, responsabile della visione centrale nitida, è danneggiata. La visione periferica rimane intatta.'
  },
  { 
    id: 'diabetic_retinopathy', 
    name: 'Retinopatia Diabetica', 
    icon: '🔴',
    description: 'Come vede chi ha retinopatia diabetica: macchie scure fluttuanti',
    filter: 'none',
    hasFloaters: true,
    explanation: 'I vasi sanguigni della retina perdono sangue, creando macchie scure nel campo visivo.'
  },
  { 
    id: 'cataract', 
    name: 'Cataratta', 
    icon: '☁️',
    description: 'Come vede chi ha la cataratta: visione opaca e offuscata',
    filter: 'blur(2px) brightness(0.8) contrast(0.7)',
    hasYellowTint: true,
    explanation: 'Il cristallino diventa opaco con l\'età, come guardare attraverso un vetro sporco.'
  },
  { 
    id: 'glaucoma', 
    name: 'Glaucoma avanzato', 
    icon: '🔲',
    description: 'Come vede chi ha glaucoma avanzato: visione a tunnel',
    filter: 'none',
    hasTunnelVision: true,
    explanation: 'Il glaucoma danneggia progressivamente il nervo ottico, riducendo prima la visione periferica.'
  }
];

export const Tests = () => {
  const navigate = useNavigate();
  const [activeTest, setActiveTest] = useState(null);
  const [currentEye, setCurrentEye] = useState('right');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  
  // Modal post-test state
  const [showTestCompletedModal, setShowTestCompletedModal] = useState(false);
  const [testCompletedResult, setTestCompletedResult] = useState(null);
  const [testCompletedName, setTestCompletedName] = useState('');
  
  // Verifica se l'utente è loggato o ha fatto lead generation
  useEffect(() => {
    const user = localStorage.getItem('user');
    const testLead = localStorage.getItem('test_lead');
    if (user || testLead) {
      setIsAuthenticated(true);
    }
  }, []);
  
  // Ishihara state
  const [ishiharaAnswers, setIshiharaAnswers] = useState({});
  const [ishiharaCurrentPlate, setIshiharaCurrentPlate] = useState(0);
  
  // OSDI state
  const [osdiAnswers, setOsdiAnswers] = useState({});
  const [osdiCompleted, setOsdiCompleted] = useState(false);
  
  // Amsler state
  const [amslerResults, setAmslerResults] = useState({ right: null, left: null });
  
  // Red-Green state
  const [redGreenResults, setRedGreenResults] = useState({ right: null, left: null });
  const [redGreenFullscreen, setRedGreenFullscreen] = useState(false);
  const [redGreenLevel, setRedGreenLevel] = useState(1); // 0.8, 0.9, 1.0 corrispondenti a livelli 0, 1, 2
  
  // Simulation state
  const [activeSimulation, setActiveSimulation] = useState(null);
  
  // Children Visual Acuity state
  const [childrenVACurrentLine, setChildrenVACurrentLine] = useState(0);
  const [childrenVACurrentE, setChildrenVACurrentE] = useState(0); // Quale E della linea (0-3)
  const [childrenVAOrientation, setChildrenVAOrientation] = useState('right');
  const [childrenVAResults, setChildrenVAResults] = useState({ right: null, left: null });
  const [childrenVACorrectCount, setChildrenVACorrectCount] = useState(0);
  const [childrenVALineErrors, setChildrenVALineErrors] = useState(0); // Errori nella linea corrente
  const [isDesktop, setIsDesktop] = useState(true);
  
  // MGD/SPEED test state (legacy - kept for backward compatibility)
  const [speedAnswers, setSpeedAnswers] = useState({});
  const [mgdAnswers, setMgdAnswers] = useState({});
  
  // DEQ-5 test state
  const [deq5Answers, setDeq5Answers] = useState({});
  
  // Contrast Sensitivity test state
  const [contrastLevel, setContrastLevel] = useState(0);
  const [contrastOrientation, setContrastOrientation] = useState(0); // 0, 90, 180, 270 gradi
  const [contrastResults, setContrastResults] = useState([]);
  const [contrastTestStarted, setContrastTestStarted] = useState(false);
  
  // Colorblind comparison state
  const [activeColorblindScene, setActiveColorblindScene] = useState('fruits');
  const [activeColorblindType, setActiveColorblindType] = useState('deuteranopia');
  
  // Check if desktop on mount
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);
  
  // Generate random E orientation
  const generateRandomOrientation = () => {
    const orientations = ['up', 'down', 'left', 'right'];
    return orientations[Math.floor(Math.random() * orientations.length)];
  };

  // Supporto tastiera per test acuità visiva bambini
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeTest !== 'children_visual_acuity') return;
      
      const keyToDirection = {
        'ArrowUp': 'up',
        'ArrowDown': 'down', 
        'ArrowLeft': 'left',
        'ArrowRight': 'right'
      };
      
      if (keyToDirection[e.key]) {
        e.preventDefault();
        // Simula click sul pulsante corrispondente
        const btn = document.querySelector(`[data-testid="va-btn-${keyToDirection[e.key]}"]`);
        if (btn) btn.click();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTest]);

  const tests = [
    { id: 'ishihara', name: 'Test di Ishihara', icon: Palette, description: 'Tavole per la diagnosi del daltonismo e deficit colori rosso-verde', color: '#E57373' },
    { id: 'colorblind_comparison', name: 'Simulazione Daltonismo', icon: Eye, description: 'Vedi come appare il mondo a chi ha deficit dei colori', color: '#9C27B0' },
    { id: 'contrast_sensitivity', name: 'Test Sensibilità al Contrasto', icon: Sun, description: '10 livelli di contrasto per valutare la sensibilità visiva', color: '#795548' },
    { id: 'children_visual_acuity', name: 'Acuità Visiva Adulti e Bambini', icon: Baby, description: 'Test con E di Albini per adulti e bambini - A 3 metri (solo desktop)', color: '#FF5722', desktopOnly: true },
    { id: 'amsler', name: 'Test di Amsler', icon: Grid, description: 'Valuta la visione centrale - rileva maculopatie e distorsioni', color: '#2A9FCB' },
    { id: 'redgreen', name: 'Test Rosso-Verde', icon: Eye, description: 'Test con E di Albini per valutare la refrazione', color: '#FF9800' },
    { id: 'osdi', name: 'OSDI-6 Occhio Secco', icon: Droplets, description: 'Questionario validato per la sindrome dell\'occhio secco', color: '#2196F3' },
    { id: 'deq5', name: 'DEQ-5 Occhio Secco', icon: Thermometer, description: 'Questionario DEQ-5 per valutare secchezza e disagio oculare', color: '#00BCD4' },
    { id: 'simulations', name: 'Simulazioni Altre Patologie', icon: Glasses, description: 'Scopri come vedono le persone con miopia, cataratta, glaucoma e altre patologie', color: '#607D8B' }
  ];

  const saveTestResult = async (testName, data) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.id) {
        await axios.post(`${API}/test-results`, {
          patient_id: user.id,
          test_name: testName,
          result_data: data
        });
        toast.success('Risultato salvato nella tua cartella clinica!');
      } else {
        toast.info('Effettua il login per salvare i risultati');
      }
    } catch (error) {
      console.error('Error saving test:', error);
    }
  };

  // Funzione per completare un test e mostrare il modal
  const completeTest = async (testName, result, isAltered = false, details = null) => {
    // Salva il risultato
    await saveTestResult(testName, result);
    
    // Mostra il modal
    setTestCompletedName(testName);
    setTestCompletedResult({
      ...result,
      altered: isAltered,
      needsAttention: isAltered,
      details: details
    });
    setShowTestCompletedModal(true);
    
    // Se il risultato è alterato, invia notifica al backend per email/SMS
    if (isAltered) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const testLead = JSON.parse(localStorage.getItem('test_lead') || '{}');
        
        const contactData = user.id ? {
          user_id: user.id,
          email: user.email,
          phone: user.phone,
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim()
        } : {
          email: testLead.email,
          name: testLead.name
        };
        
        await axios.post(`${API}/test-results/alert`, {
          test_name: testName,
          result_summary: details || 'Risultato alterato - consigliato controllo',
          contact: contactData
        });
        
        console.log('Alert inviato per risultato alterato');
      } catch (error) {
        console.error('Errore invio alert:', error);
      }
    }
  };

  // Funzione per prenotare visita dal modal
  const handleBookVisitFromModal = () => {
    setShowTestCompletedModal(false);
    navigate('/booking');
  };

  // ==================== ISHIHARA TEST ====================
  const renderIshiharaTest = () => {
    const currentPlate = ISHIHARA_PLATES[ishiharaCurrentPlate];
    const isLastPlate = ishiharaCurrentPlate === ISHIHARA_PLATES.length - 1;
    
    const calculateIshiharaScore = () => {
      let correct = 0;
      let possibleColorBlindness = 0;
      
      ISHIHARA_PLATES.forEach((plate, idx) => {
        const answer = ishiharaAnswers[idx]?.toLowerCase().trim();
        
        if (plate.type === 'hidden') {
          // Tavole nascoste: visione normale NON vede numeri
          // Se rispondono con altNumber (es. "2") = probabile daltonismo
          if (!answer || answer === 'nessuno' || answer === 'nulla' || answer === 'non vedo' || answer === '') {
            correct++;
          } else if (answer === plate.altNumber?.toLowerCase()) {
            possibleColorBlindness++;
          }
        } else if (plate.type === 'transformation') {
          // Tavole di trasformazione: visione normale vede number, daltonici vedono altNumber
          if (answer === plate.number?.toLowerCase()) {
            correct++;
          } else if (answer === plate.altNumber?.toLowerCase()) {
            possibleColorBlindness++;
          }
        } else {
          // Tavole normali (demo, vanishing, protan_deutan): corretto se vede il numero giusto
          if (answer === plate.number?.toLowerCase()) correct++;
        }
      });
      
      return { 
        correct, 
        total: ISHIHARA_PLATES.length, 
        percentage: Math.round((correct / ISHIHARA_PLATES.length) * 100),
        possibleColorBlindness 
      };
    };

    return (
      <div className="space-y-6" data-testid="ishihara-test">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2" style={{color: '#2A9FCB'}}>Test di Ishihara</h3>
          <p className="text-[#2A9FCB]/80 mb-4">Tavole per la diagnosi del daltonismo</p>
        </div>

        {/* Avviso importante sulle tavole originali */}
        <div className="p-4 rounded-xl mb-6" style={{background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)'}}>
          <h4 className="font-semibold flex items-center gap-2 mb-2" style={{color: '#DC2626'}}>
            <AlertCircle className="w-5 h-5" />
            Importante: Tavole Originali
          </h4>
          <p className="text-sm text-[#2A9FCB] mb-2">
            Per una diagnosi accurata del daltonismo è necessario utilizzare le <strong>tavole di Ishihara originali</strong> 
            (libro stampato), poiché i colori su schermo possono variare significativamente.
          </p>
          <p className="text-sm text-[#2A9FCB]/80">
            Questo test digitale fornisce solo un'indicazione preliminare. Per una diagnosi definitiva, 
            consultare un oculista con le tavole ufficiali.
          </p>
        </div>

        {/* Istruzioni */}
        <div className="p-4 rounded-xl mb-6" style={{background: 'rgba(90, 155, 130, 0.1)', border: '1px solid rgba(90, 155, 130, 0.3)'}}>
          <h4 className="font-semibold flex items-center gap-2 mb-2" style={{color: '#2A9FCB'}}>
            <Info className="w-5 h-5" />
            Come eseguire il test
          </h4>
          <ul className="text-sm text-[#2A9FCB]/80 space-y-1">
            <li>• Esegui il test in ambiente ben illuminato</li>
            <li>• Tieni lo schermo a circa 50cm dagli occhi</li>
            <li>• Se porti occhiali da vista, indossali durante il test</li>
            <li>• Scrivi il numero che vedi in ogni tavola (o "nulla" se non vedi numeri)</li>
          </ul>
        </div>

        {/* Tavola attuale con immagini reali */}
        <div className="text-center">
          <p className="text-sm text-[#2A9FCB]/70 mb-4">Tavola {ishiharaCurrentPlate + 1} di {ISHIHARA_PLATES.length}</p>
          
          {/* Tavola Ishihara - Immagine Ufficiale */}
          <div className="flex justify-center mb-4">
            <div className="rounded-full overflow-hidden shadow-lg bg-white flex items-center justify-center" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)', width: '350px', height: '350px' }}>
              <img 
                src={currentPlate.image} 
                alt={`Tavola Ishihara ${ishiharaCurrentPlate + 1}`}
                className="w-full h-full object-contain"
                style={{ borderRadius: '50%' }}
                data-testid={`ishihara-plate-${ishiharaCurrentPlate}`}
              />
            </div>
          </div>

          <p className="text-xs text-[#2A9FCB]/70 mb-4">{currentPlate.description}</p>

          {/* Input risposta */}
          <div className="max-w-xs mx-auto">
            <input
              type="text"
              placeholder="Che numero vedi?"
              value={ishiharaAnswers[ishiharaCurrentPlate] || ''}
              onChange={(e) => setIshiharaAnswers({...ishiharaAnswers, [ishiharaCurrentPlate]: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 text-center text-xl font-semibold focus:outline-none"
              style={{borderColor: '#2A9FCB'}}
              data-testid={`ishihara-input-${ishiharaCurrentPlate}`}
            />
          </div>
        </div>

        {/* Navigazione */}
        <div className="flex justify-center gap-4 mt-6">
          {ishiharaCurrentPlate > 0 && (
            <button
              onClick={() => setIshiharaCurrentPlate(p => p - 1)}
              className="px-6 py-3 rounded-xl border-2 font-medium"
              style={{borderColor: '#2A9FCB', color: '#2A9FCB'}}
            >
              Precedente
            </button>
          )}
          
          {isLastPlate ? (
            <button
              onClick={() => {
                const score = calculateIshiharaScore();
                const isAltered = score.percentage < 80;
                const details = isAltered 
                  ? `Punteggio: ${score.correct}/${score.total} (${score.percentage.toFixed(0)}%). Possibile deficit nella percezione dei colori. Si consiglia una valutazione con tavole originali di Ishihara.`
                  : `Punteggio: ${score.correct}/${score.total} (${score.percentage.toFixed(0)}%). Visione dei colori nella norma.`;
                
                completeTest('Test di Ishihara', { answers: ishiharaAnswers, score }, isAltered, details);
                
                if (score.percentage >= 80) {
                  toast.success(`Ottimo! ${score.correct}/${score.total} corrette - Indicazione preliminare positiva`);
                } else if (score.percentage >= 50) {
                  toast.warning(`${score.correct}/${score.total} - Possibile deficit. Consigliato test con tavole originali`);
                } else {
                  toast.error(`${score.correct}/${score.total} - Sospetto daltonismo. Necessaria visita specialistica`);
                }
              }}
              className="px-8 py-3 rounded-xl font-semibold text-white"
              style={{background: '#2A9FCB'}}
              data-testid="ishihara-submit"
            >
              Completa Test
            </button>
          ) : (
            <button
              onClick={() => setIshiharaCurrentPlate(p => p + 1)}
              className="px-8 py-3 rounded-xl font-semibold text-white flex items-center gap-2"
              style={{background: '#2A9FCB'}}
            >
              Prossima <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  };

  // ==================== AMSLER TEST ====================
  const renderAmslerTest = () => (
    <div className="space-y-6" data-testid="amsler-test">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2" style={{color: '#2A9FCB'}}>Test di Amsler</h3>
        <p className="text-[#2A9FCB]/80">Rileva problemi alla macula e distorsioni della visione centrale</p>
      </div>

      {/* Istruzioni dettagliate */}
      <div className="p-6 rounded-xl" style={{background: 'rgba(90, 155, 130, 0.1)', border: '1px solid rgba(90, 155, 130, 0.3)'}}>
        <h4 className="font-semibold flex items-center gap-2 mb-4" style={{color: '#2A9FCB'}}>
          <Info className="w-5 h-5" />
          Istruzioni per l'esecuzione
        </h4>
        <ol className="text-sm text-[#2A9FCB] space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{background: '#2A9FCB'}}>1</span>
            <span>Indossa gli occhiali da lettura se li usi normalmente</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{background: '#2A9FCB'}}>2</span>
            <span>Posizionati a circa 30-40 cm dallo schermo</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{background: '#2A9FCB'}}>3</span>
            <span><strong>COPRI UN OCCHIO</strong> con la mano (senza premere)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{background: '#2A9FCB'}}>4</span>
            <span>Fissa il punto centrale con l'occhio scoperto</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{background: '#2A9FCB'}}>5</span>
            <span>Senza muovere lo sguardo, nota se le linee appaiono distorte, ondulate o mancanti</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{background: '#2A9FCB'}}>6</span>
            <span><strong>RIPETI con l'altro occhio</strong></span>
          </li>
        </ol>
      </div>

      {/* Selezione occhio */}
      <div className="flex justify-center gap-4">
        <button
          onClick={() => setCurrentEye('right')}
          className={`px-6 py-3 rounded-xl font-medium transition-all ${currentEye === 'right' ? 'text-white' : ''}`}
          style={{
            background: currentEye === 'right' ? '#2A9FCB' : '#F3F4F6',
            color: currentEye === 'right' ? '#fff' : '#2A9FCB'
          }}
        >
          👁️ Occhio DESTRO
        </button>
        <button
          onClick={() => setCurrentEye('left')}
          className={`px-6 py-3 rounded-xl font-medium transition-all ${currentEye === 'left' ? 'text-white' : ''}`}
          style={{
            background: currentEye === 'left' ? '#2A9FCB' : '#F3F4F6',
            color: currentEye === 'left' ? '#fff' : '#2A9FCB'
          }}
        >
          👁️ Occhio SINISTRO
        </button>
      </div>

      <p className="text-center text-lg font-semibold" style={{color: '#2A9FCB'}}>
        Copri l'occhio {currentEye === 'right' ? 'SINISTRO' : 'DESTRO'} e fissa il punto centrale
      </p>

      {/* Griglia di Amsler */}
      <div className="flex justify-center">
        <div className="relative w-80 h-80 bg-white border-4 rounded-lg" style={{borderColor: '#2A9FCB'}}>
          {/* Griglia */}
          <svg className="absolute inset-0 w-full h-full">
            {/* Linee verticali */}
            {[...Array(20)].map((_, i) => (
              <line 
                key={`v-${i}`} 
                x1={`${(i + 1) * 5}%`} 
                y1="0" 
                x2={`${(i + 1) * 5}%`} 
                y2="100%" 
                stroke="#333" 
                strokeWidth="0.5" 
              />
            ))}
            {/* Linee orizzontali */}
            {[...Array(20)].map((_, i) => (
              <line 
                key={`h-${i}`} 
                x1="0" 
                y1={`${(i + 1) * 5}%`} 
                x2="100%" 
                y2={`${(i + 1) * 5}%`} 
                stroke="#333" 
                strokeWidth="0.5" 
              />
            ))}
          </svg>
          {/* Punto centrale */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-red-600 rounded-full"></div>
        </div>
      </div>

      {/* Risposta */}
      <div className="text-center space-y-4">
        <p className="font-medium text-[#2A9FCB]">Come appaiono le linee con l'occhio {currentEye === 'right' ? 'destro' : 'sinistro'}?</p>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => {
              const newResults = {...amslerResults, [currentEye]: 'normal'};
              setAmslerResults(newResults);
              if (currentEye === 'right') {
                setCurrentEye('left');
                toast.info('Ora testa l\'occhio sinistro');
              } else {
                const isAltered = newResults.right === 'distorted' || newResults.left === 'distorted';
                const details = isAltered 
                  ? `Distorsioni rilevate. OD: ${newResults.right === 'distorted' ? 'Distorto' : 'Normale'}, OS: ${newResults.left === 'distorted' ? 'Distorto' : 'Normale'}. Consigliata visita oculistica urgente per escludere maculopatie.`
                  : 'Nessuna distorsione rilevata in entrambi gli occhi. Visione centrale nella norma.';
                completeTest('Test di Amsler', newResults, isAltered, details);
                if (newResults.right === 'normal' && newResults.left === 'normal') {
                  toast.success('Ottimo! Nessuna distorsione rilevata in entrambi gli occhi');
                } else {
                  toast.warning('Rilevate distorsioni. Consigliata visita oculistica');
                }
              }
            }}
            className="px-6 py-3 rounded-xl font-medium text-white flex items-center gap-2"
            style={{background: '#22C55E'}}
            data-testid={`amsler-normal-${currentEye}`}
          >
            <CheckCircle className="w-5 h-5" />
            Tutte dritte e regolari
          </button>
          <button
            onClick={() => {
              const newResults = {...amslerResults, [currentEye]: 'distorted'};
              setAmslerResults(newResults);
              if (currentEye === 'right') {
                setCurrentEye('left');
                toast.warning('Distorsione rilevata. Ora testa l\'occhio sinistro');
              } else {
                const details = `Distorsioni rilevate. OD: ${newResults.right === 'distorted' ? 'Distorto' : 'Normale'}, OS: ${newResults.left === 'distorted' ? 'Distorto' : 'Normale'}. Consigliata visita oculistica urgente per escludere maculopatie.`;
                completeTest('Test di Amsler', newResults, true, details);
                toast.error('Distorsioni rilevate. Consigliamo una visita oculistica urgente');
              }
            }}
            className="px-6 py-3 rounded-xl font-medium text-white flex items-center gap-2"
            style={{background: '#EF4444'}}
            data-testid={`amsler-distorted-${currentEye}`}
          >
            <AlertCircle className="w-5 h-5" />
            Ondulate/Distorte/Mancanti
          </button>
        </div>
      </div>

      {/* Risultati */}
      {(amslerResults.right || amslerResults.left) && (
        <div className="mt-6 p-4 rounded-xl" style={{background: '#F3F4F6'}}>
          <h4 className="font-semibold mb-2">Risultati:</h4>
          <div className="flex gap-6">
            <div>
              <span className="text-sm text-[#2A9FCB]/70">Occhio Destro:</span>
              <span className={`ml-2 font-medium ${amslerResults.right === 'normal' ? 'text-green-600' : amslerResults.right === 'distorted' ? 'text-red-600' : 'text-[#2A9FCB]/60'}`}>
                {amslerResults.right === 'normal' ? '✓ Normale' : amslerResults.right === 'distorted' ? '⚠️ Distorsioni' : 'Non testato'}
              </span>
            </div>
            <div>
              <span className="text-sm text-[#2A9FCB]/70">Occhio Sinistro:</span>
              <span className={`ml-2 font-medium ${amslerResults.left === 'normal' ? 'text-green-600' : amslerResults.left === 'distorted' ? 'text-red-600' : 'text-[#2A9FCB]/60'}`}>
                {amslerResults.left === 'normal' ? '✓ Normale' : amslerResults.left === 'distorted' ? '⚠️ Distorsioni' : 'Non testato'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ==================== RED-GREEN TEST (Duochrome Bicromatico) ====================
  // Lettere per ogni livello di acuità (0.8, 0.9, 1.0)
  const redGreenLetters = {
    red: [
      ['A', 'T'],   // Riga 1
      ['D', 'N'],   // Riga 2
      ['N', 'O']    // Riga 3
    ],
    green: [
      ['R', 'N'],   // Riga 1
      ['E', 'H'],   // Riga 2
      ['R', 'N']    // Riga 3
    ]
  };
  
  const acuityLevels = ['0.8', '0.9', '1.0'];
  
  // Fullscreen content for Red-Green test
  const renderRedGreenFullscreen = () => (
    <div 
      className="fixed inset-0 z-50 bg-gray-800 flex flex-col"
      data-testid="redgreen-fullscreen"
    >
      {/* Header controls */}
      <div className="absolute top-4 right-4 z-10 flex gap-2">
        <button
          onClick={() => setRedGreenFullscreen(false)}
          className="p-3 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
          title="Esci da schermo intero"
        >
          <Minimize2 className="w-6 h-6 text-gray-700" />
        </button>
      </div>
      
      {/* Eye indicator */}
      <div className="absolute top-4 left-4 z-10">
        <div className="px-4 py-2 rounded-full bg-white/90 shadow-lg">
          <span className="font-bold text-gray-800">
            👁️ Occhio {currentEye === 'right' ? 'DESTRO' : 'SINISTRO'}
          </span>
          <span className="ml-2 text-gray-600">
            (copri l'occhio {currentEye === 'right' ? 'sinistro' : 'destro'})
          </span>
        </div>
      </div>
      
      {/* Main duochrome panel */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="flex w-full max-w-5xl h-[70vh] rounded-3xl overflow-hidden shadow-2xl">
          {/* Red panel */}
          <div 
            className="flex-1 flex flex-col items-center justify-center p-6"
            style={{ background: '#DC2626' }}
          >
            {redGreenLetters.red.map((row, rowIndex) => (
              <div key={rowIndex} className="flex gap-8 mb-4">
                {row.map((letter, colIndex) => (
                  <span 
                    key={colIndex}
                    className="text-black font-bold select-none"
                    style={{ 
                      fontFamily: "'Optician Sans', 'Arial Black', sans-serif",
                      fontSize: `${8 - rowIndex * 1.5}rem`,
                      lineHeight: 1,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    {letter}
                  </span>
                ))}
              </div>
            ))}
          </div>
          
          {/* Green panel */}
          <div 
            className="flex-1 flex flex-col items-center justify-center p-6"
            style={{ background: '#16A34A' }}
          >
            {redGreenLetters.green.map((row, rowIndex) => (
              <div key={rowIndex} className="flex gap-8 mb-4">
                {row.map((letter, colIndex) => (
                  <span 
                    key={colIndex}
                    className="text-black font-bold select-none"
                    style={{ 
                      fontFamily: "'Optician Sans', 'Arial Black', sans-serif",
                      fontSize: `${8 - rowIndex * 1.5}rem`,
                      lineHeight: 1,
                      textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    {letter}
                  </span>
                ))}
              </div>
            ))}
          </div>
          
          {/* Acuity scale on the right */}
          <div className="w-16 bg-gray-700 flex flex-col items-center justify-center py-8">
            {acuityLevels.map((level, index) => (
              <span 
                key={index}
                className="text-white font-bold mb-12 last:mb-0"
                style={{ fontSize: '1.5rem' }}
              >
                {level}
              </span>
            ))}
          </div>
        </div>
      </div>
      
      {/* Response buttons */}
      <div className="p-6 bg-gray-900">
        <p className="text-center text-white mb-4 text-lg">
          Le lettere sono più nitide su sfondo rosso o verde?
        </p>
        <div className="flex justify-center gap-4 flex-wrap">
          <button
            onClick={() => {
              const newResults = {...redGreenResults, [currentEye]: 'red'};
              setRedGreenResults(newResults);
              if (currentEye === 'right') {
                setCurrentEye('left');
                toast.info("Ora testa l'occhio sinistro");
              } else {
                setRedGreenFullscreen(false);
                saveTestResult('RedGreen', newResults);
                toast.info('Test completato!');
              }
            }}
            className="px-10 py-4 rounded-xl font-bold text-white text-lg transition-transform hover:scale-105"
            style={{ background: '#DC2626' }}
            data-testid="redgreen-btn-red"
          >
            🔴 Sfondo ROSSO
          </button>
          <button
            onClick={() => {
              const newResults = {...redGreenResults, [currentEye]: 'green'};
              setRedGreenResults(newResults);
              if (currentEye === 'right') {
                setCurrentEye('left');
                toast.info("Ora testa l'occhio sinistro");
              } else {
                setRedGreenFullscreen(false);
                saveTestResult('RedGreen', newResults);
                toast.info('Test completato!');
              }
            }}
            className="px-10 py-4 rounded-xl font-bold text-white text-lg transition-transform hover:scale-105"
            style={{ background: '#16A34A' }}
            data-testid="redgreen-btn-green"
          >
            🟢 Sfondo VERDE
          </button>
          <button
            onClick={() => {
              const newResults = {...redGreenResults, [currentEye]: 'equal'};
              setRedGreenResults(newResults);
              if (currentEye === 'right') {
                setCurrentEye('left');
                toast.info("Ora testa l'occhio sinistro");
              } else {
                setRedGreenFullscreen(false);
                saveTestResult('RedGreen', newResults);
                toast.success('Ottimo! Correzione ben bilanciata');
              }
            }}
            className="px-10 py-4 rounded-xl font-bold text-lg border-2 border-white text-white transition-transform hover:scale-105 hover:bg-white/10"
            data-testid="redgreen-btn-equal"
          >
            ✓ UGUALI
          </button>
        </div>
        <p className="text-center text-gray-400 text-sm mt-4">
          Rosso più nitido = possibile miopia residua | Verde più nitido = possibile ipermetropia residua | Uguale = correzione ottimale
        </p>
      </div>
    </div>
  );
  
  const renderRedGreenTest = () => (
    <div className="space-y-6" data-testid="redgreen-test">
      {/* Fullscreen overlay */}
      {redGreenFullscreen && renderRedGreenFullscreen()}
      
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2" style={{color: '#2A9FCB'}}>Test Rosso-Verde (Duochrome)</h3>
        <p className="text-[#2A9FCB]/80">Test bicromatico per valutare la correzione ottica</p>
      </div>

      {/* Istruzioni IMPORTANTI */}
      <div className="p-6 rounded-xl bg-amber-50 border-2 border-amber-300">
        <h4 className="font-bold flex items-center gap-2 mb-4 text-amber-800 text-lg">
          <AlertCircle className="w-6 h-6" />
          ⚠️ ISTRUZIONI IMPORTANTI
        </h4>
        <ol className="text-amber-900 space-y-3">
          <li className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold bg-amber-600">1</span>
            <span className="text-lg"><strong>Indossa i tuoi occhiali abituali</strong> (se li porti)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold bg-amber-600">2</span>
            <span className="text-lg"><strong>Posizionati a 3 METRI dallo schermo</strong> - questo è fondamentale per la corretta esecuzione del test</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold bg-amber-600">3</span>
            <span className="text-lg"><strong>COPRI UN OCCHIO</strong> con la mano (inizia con l'occhio sinistro coperto)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold bg-amber-600">4</span>
            <span className="text-lg">Guarda le lettere: sono più nitide su <strong>sfondo rosso</strong> o <strong>verde</strong>?</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold bg-amber-600">5</span>
            <span className="text-lg"><strong>Ripeti con l'altro occhio</strong></span>
          </li>
        </ol>
      </div>

      {/* Selezione occhio */}
      <div className="flex justify-center gap-4">
        <button
          onClick={() => setCurrentEye('right')}
          className={`px-6 py-3 rounded-xl font-medium transition-all`}
          style={{
            background: currentEye === 'right' ? '#2A9FCB' : '#F3F4F6',
            color: currentEye === 'right' ? '#fff' : '#2A9FCB'
          }}
        >
          👁️ Occhio DESTRO
        </button>
        <button
          onClick={() => setCurrentEye('left')}
          className={`px-6 py-3 rounded-xl font-medium transition-all`}
          style={{
            background: currentEye === 'left' ? '#2A9FCB' : '#F3F4F6',
            color: currentEye === 'left' ? '#fff' : '#2A9FCB'
          }}
        >
          👁️ Occhio SINISTRO
        </button>
      </div>

      <p className="text-center font-bold text-lg text-[#2A9FCB]">
        Copri l'occhio {currentEye === 'right' ? 'SINISTRO' : 'DESTRO'}
      </p>

      {/* Pulsante schermo intero */}
      <div className="text-center">
        <button
          onClick={() => setRedGreenFullscreen(true)}
          className="px-8 py-4 rounded-xl font-bold text-white text-lg flex items-center gap-3 mx-auto transition-transform hover:scale-105"
          style={{ background: '#2A9FCB' }}
          data-testid="redgreen-fullscreen-btn"
        >
          <Maximize2 className="w-6 h-6" />
          AVVIA TEST A SCHERMO INTERO
        </button>
        <p className="text-sm text-gray-500 mt-2">
          Consigliato per una corretta visualizzazione a 3 metri
        </p>
      </div>

      {/* Anteprima tavola duochrome */}
      <div className="mt-6">
        <p className="text-center text-gray-600 mb-3">Anteprima del test:</p>
        <div className="flex justify-center">
          <div className="flex w-full max-w-2xl rounded-2xl overflow-hidden shadow-lg">
            {/* Red panel preview */}
            <div 
              className="flex-1 flex flex-col items-center justify-center p-4"
              style={{ background: '#DC2626', minHeight: '200px' }}
            >
              {redGreenLetters.red.map((row, rowIndex) => (
                <div key={rowIndex} className="flex gap-4 mb-2">
                  {row.map((letter, colIndex) => (
                    <span 
                      key={colIndex}
                      className="text-black font-bold select-none"
                      style={{ 
                        fontFamily: "'Arial Black', sans-serif",
                        fontSize: `${2.5 - rowIndex * 0.4}rem`,
                        lineHeight: 1
                      }}
                    >
                      {letter}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            
            {/* Green panel preview */}
            <div 
              className="flex-1 flex flex-col items-center justify-center p-4"
              style={{ background: '#16A34A', minHeight: '200px' }}
            >
              {redGreenLetters.green.map((row, rowIndex) => (
                <div key={rowIndex} className="flex gap-4 mb-2">
                  {row.map((letter, colIndex) => (
                    <span 
                      key={colIndex}
                      className="text-black font-bold select-none"
                      style={{ 
                        fontFamily: "'Arial Black', sans-serif",
                        fontSize: `${2.5 - rowIndex * 0.4}rem`,
                        lineHeight: 1
                      }}
                    >
                      {letter}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            
            {/* Acuity scale */}
            <div className="w-12 bg-gray-700 flex flex-col items-center justify-center py-4">
              {acuityLevels.map((level, index) => (
                <span 
                  key={index}
                  className="text-white font-bold mb-6 last:mb-0 text-sm"
                >
                  {level}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Risultati */}
      {(redGreenResults.right || redGreenResults.left) && (
        <div className="mt-6 p-4 rounded-xl" style={{background: '#F3F4F6'}}>
          <h4 className="font-semibold mb-2">Risultati:</h4>
          <div className="flex gap-6">
            <div>
              <span className="text-sm text-[#2A9FCB]/70">Occhio Destro:</span>
              <span className="ml-2 font-medium">
                {redGreenResults.right === 'red' ? '🔴 Rosso più nitido' : 
                 redGreenResults.right === 'green' ? '🟢 Verde più nitido' : 
                 redGreenResults.right === 'equal' ? '✓ Uguale' : 'Non testato'}
              </span>
            </div>
            <div>
              <span className="text-sm text-[#2A9FCB]/70">Occhio Sinistro:</span>
              <span className="ml-2 font-medium">
                {redGreenResults.left === 'red' ? '🔴 Rosso più nitido' : 
                 redGreenResults.left === 'green' ? '🟢 Verde più nitido' : 
                 redGreenResults.left === 'equal' ? '✓ Uguale' : 'Non testato'}
              </span>
            </div>
          </div>
          
          {/* Interpretazione risultati */}
          {redGreenResults.right && redGreenResults.left && (
            <div className="mt-4 p-4 rounded-lg bg-white border border-gray-200">
              <h5 className="font-semibold text-gray-800 mb-2">Interpretazione:</h5>
              <ul className="text-sm text-gray-700 space-y-1">
                {redGreenResults.right === 'red' && (
                  <li>• <strong>Occhio destro:</strong> Possibile miopia residua - le lenti potrebbero necessitare una correzione più forte</li>
                )}
                {redGreenResults.right === 'green' && (
                  <li>• <strong>Occhio destro:</strong> Possibile ipermetropia residua o ipercorrezione miopica</li>
                )}
                {redGreenResults.right === 'equal' && (
                  <li>• <strong>Occhio destro:</strong> Correzione ottimale - le lenti sono ben calibrate</li>
                )}
                {redGreenResults.left === 'red' && (
                  <li>• <strong>Occhio sinistro:</strong> Possibile miopia residua - le lenti potrebbero necessitare una correzione più forte</li>
                )}
                {redGreenResults.left === 'green' && (
                  <li>• <strong>Occhio sinistro:</strong> Possibile ipermetropia residua o ipercorrezione miopica</li>
                )}
                {redGreenResults.left === 'equal' && (
                  <li>• <strong>Occhio sinistro:</strong> Correzione ottimale - le lenti sono ben calibrate</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ==================== OSDI-6 TEST ====================
  const renderOSDITest = () => {
    const calculateOSDIScore = () => {
      const answeredCount = Object.keys(osdiAnswers).length;
      if (answeredCount === 0) return 0;
      
      const totalScore = Object.values(osdiAnswers).reduce((sum, val) => sum + val, 0);
      return Math.round((totalScore * 25) / answeredCount);
    };

    const getOSDIInterpretation = (score) => {
      if (score <= 12) return { level: 'Normale', color: '#22C55E', description: 'Nessun segno significativo di occhio secco' };
      if (score <= 22) return { level: 'Lieve', color: '#EAB308', description: 'Sindrome dell\'occhio secco lieve' };
      if (score <= 32) return { level: 'Moderato', color: '#F97316', description: 'Sindrome dell\'occhio secco moderata' };
      return { level: 'Severo', color: '#EF4444', description: 'Sindrome dell\'occhio secco severa - Consigliata visita' };
    };

    const osdiScore = calculateOSDIScore();
    const interpretation = getOSDIInterpretation(osdiScore);
    const allAnswered = Object.keys(osdiAnswers).length === OSDI_QUESTIONS.length;

    return (
      <div className="space-y-6" data-testid="osdi-test">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2" style={{color: '#2A9FCB'}}>OSDI-6: Test Occhio Secco</h3>
          <p className="text-[#2A9FCB]/80">Ocular Surface Disease Index - Questionario validato</p>
        </div>

        {/* Istruzioni */}
        <div className="p-4 rounded-xl" style={{background: 'rgba(33, 150, 243, 0.1)', border: '1px solid rgba(33, 150, 243, 0.3)'}}>
          <h4 className="font-semibold flex items-center gap-2 mb-2" style={{color: '#2196F3'}}>
            <Droplets className="w-5 h-5" />
            Informazioni sul test
          </h4>
          <p className="text-sm text-[#2A9FCB]/80">
            Rispondi pensando all'ultima settimana. Indica quanto frequentemente hai sperimentato 
            ciascun sintomo o difficoltà.
          </p>
        </div>

        {/* Domande */}
        <div className="space-y-6">
          {OSDI_QUESTIONS.map((q, idx) => (
            <div key={q.id} className="p-6 rounded-xl bg-white shadow-sm" style={{border: '1px solid #E5E7EB'}}>
              <p className="font-medium mb-4" style={{color: '#2A9FCB'}}>
                {idx + 1}. {q.question}
              </p>
              <div className="flex flex-wrap gap-2">
                {OSDI_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setOsdiAnswers({...osdiAnswers, [q.id]: option.value})}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      osdiAnswers[q.id] === option.value ? 'text-white' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                    style={{
                      background: osdiAnswers[q.id] === option.value ? '#2196F3' : undefined
                    }}
                    data-testid={`osdi-q${q.id}-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Risultato */}
        {allAnswered && (
          <div className="p-6 rounded-xl text-center" style={{background: `${interpretation.color}15`, border: `2px solid ${interpretation.color}`}}>
            <div className="text-5xl font-bold mb-2" style={{color: interpretation.color}}>
              {osdiScore}
            </div>
            <div className="text-xl font-semibold mb-1" style={{color: interpretation.color}}>
              {interpretation.level}
            </div>
            <p className="text-[#2A9FCB]/80">{interpretation.description}</p>
          </div>
        )}

        {/* Pulsante salva */}
        <div className="text-center">
          <button
            onClick={() => {
              if (!allAnswered) {
                toast.error('Rispondi a tutte le domande');
                return;
              }
              saveTestResult('OSDI-6', { 
                answers: osdiAnswers, 
                score: osdiScore, 
                interpretation: interpretation.level 
              });
              setOsdiCompleted(true);
              toast.success(`Punteggio OSDI: ${osdiScore} - ${interpretation.level}`);
            }}
            disabled={!allAnswered}
            className="px-8 py-4 rounded-xl font-semibold text-white disabled:opacity-50"
            style={{background: '#2196F3'}}
            data-testid="osdi-submit"
          >
            Calcola Punteggio OSDI
          </button>
        </div>

        {/* Scala interpretazione */}
        <div className="p-4 rounded-xl" style={{background: '#F3F4F6'}}>
          <h4 className="font-semibold mb-3">Scala di interpretazione OSDI:</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="p-2 rounded text-center" style={{background: '#22C55E20', color: '#166534'}}>
              0-12: Normale
            </div>
            <div className="p-2 rounded text-center" style={{background: '#EAB30820', color: '#A16207'}}>
              13-22: Lieve
            </div>
            <div className="p-2 rounded text-center" style={{background: '#F9731620', color: '#C2410C'}}>
              23-32: Moderato
            </div>
            <div className="p-2 rounded text-center" style={{background: '#EF444420', color: '#DC2626'}}>
              33-100: Severo
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== VISION SIMULATIONS ====================
  const renderSimulationsTest = () => {
    const selectedSim = VISION_SIMULATIONS.find(s => s.id === activeSimulation);

    return (
      <div className="space-y-6" data-testid="simulations-test">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2" style={{color: '#2A9FCB'}}>Simulazioni Visive</h3>
          <p className="text-[#2A9FCB]/80">Scopri come vedono le persone con diverse patologie oculari</p>
        </div>

        {/* Info */}
        <div className="p-4 rounded-xl" style={{background: 'rgba(156, 39, 176, 0.1)', border: '1px solid rgba(156, 39, 176, 0.3)'}}>
          <h4 className="font-semibold flex items-center gap-2 mb-2" style={{color: '#9C27B0'}}>
            <Glasses className="w-5 h-5" />
            A cosa servono queste simulazioni?
          </h4>
          <p className="text-sm text-[#2A9FCB]/80">
            Queste simulazioni aiutano a comprendere come le diverse patologie oculari influenzano la visione.
            Utile per familiari, caregiver e chiunque voglia capire meglio le difficoltà visive.
          </p>
        </div>

        {/* Selezione simulazione */}
        <div className="grid md:grid-cols-3 gap-4">
          {VISION_SIMULATIONS.map((sim) => (
            <button
              key={sim.id}
              onClick={() => setActiveSimulation(activeSimulation === sim.id ? null : sim.id)}
              className={`p-4 rounded-xl text-left transition-all hover:shadow-lg ${
                activeSimulation === sim.id ? 'ring-2 ring-purple-500' : ''
              }`}
              style={{
                background: activeSimulation === sim.id ? 'rgba(156, 39, 176, 0.1)' : 'white',
                border: '1px solid #E5E7EB'
              }}
              data-testid={`sim-${sim.id}`}
            >
              <span className="text-3xl mb-2 block">{sim.icon}</span>
              <h4 className="font-semibold" style={{color: '#2A9FCB'}}>{sim.name}</h4>
              <p className="text-xs text-[#2A9FCB]/70 mt-1">{sim.description}</p>
            </button>
          ))}
        </div>

        {/* Area di simulazione */}
        {selectedSim && (
          <div className="mt-8">
            <h4 className="text-xl font-semibold mb-4 text-center" style={{color: '#9C27B0'}}>
              {selectedSim.name}: Confronto Visione
            </h4>
            
            {/* Spiegazione */}
            <div className="mb-6 p-4 rounded-xl bg-purple-50 text-center">
              <p className="text-sm text-[#2A9FCB]">{selectedSim.explanation}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Visione normale */}
              <div className="text-center">
                <p className="font-semibold mb-3 text-green-600">👁️ Visione Normale</p>
                <div 
                  className="rounded-xl overflow-hidden relative"
                  style={{
                    background: selectedSim.isNight ? '#1a1a2e' : '#87CEEB',
                    height: '250px'
                  }}
                >
                  {/* Scena */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    {selectedSim.isNight ? (
                      // Scena notturna con luci
                      <div className="relative w-full h-full">
                        <div className="absolute top-10 left-1/4 w-4 h-4 bg-yellow-300 rounded-full shadow-lg shadow-yellow-300/50"></div>
                        <div className="absolute top-10 right-1/4 w-4 h-4 bg-yellow-300 rounded-full shadow-lg shadow-yellow-300/50"></div>
                        <div className="absolute top-20 left-1/3 w-3 h-3 bg-white rounded-full shadow-lg shadow-white/50"></div>
                        <div className="absolute top-20 right-1/3 w-3 h-3 bg-white rounded-full shadow-lg shadow-white/50"></div>
                        <div className="absolute bottom-0 w-full h-16 bg-gray-800"></div>
                        <Moon className="absolute top-5 right-10 w-8 h-8 text-yellow-100" />
                        {/* Strada */}
                        <div className="absolute bottom-16 left-0 right-0 h-1 bg-white/50"></div>
                        <p className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-white text-sm">STRADA</p>
                      </div>
                    ) : (
                      // Scena diurna
                      <div className="relative w-full h-full">
                        <Sun className="absolute top-5 right-10 w-10 h-10 text-yellow-400" />
                        {/* Montagne */}
                        <div className="absolute bottom-16 left-0 right-0">
                          <svg viewBox="0 0 200 50" className="w-full h-16">
                            <polygon points="0,50 40,10 80,50" fill="#4a7c59" />
                            <polygon points="60,50 100,15 140,50" fill="#3d6b4a" />
                            <polygon points="120,50 160,20 200,50" fill="#4a7c59" />
                          </svg>
                        </div>
                        {/* Prato */}
                        <div className="absolute bottom-0 w-full h-16 bg-green-500"></div>
                        {/* Albero */}
                        <div className="absolute bottom-16 left-1/4">
                          <div className="w-16 h-16 bg-green-700 rounded-full"></div>
                          <div className="w-3 h-8 bg-amber-700 mx-auto"></div>
                        </div>
                        {/* Testo */}
                        <p className="absolute bottom-20 right-10 text-[#2A9FCB] font-bold text-lg">TESTO</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Visione con patologia */}
              <div className="text-center">
                <p className="font-semibold mb-3 text-red-600">{selectedSim.icon} {selectedSim.name}</p>
                <div 
                  className="rounded-xl overflow-hidden relative"
                  style={{
                    background: selectedSim.isNight ? '#1a1a2e' : '#87CEEB',
                    height: '250px',
                    filter: selectedSim.filter,
                  }}
                >
                  {/* Stessa scena ma con effetti patologia */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    {selectedSim.isNight ? (
                      // Scena notturna con effetto astigmatismo (luci allungate)
                      <div className="relative w-full h-full">
                        {/* Luci con effetto streak */}
                        <div className="absolute top-10 left-1/4 w-4 h-4 bg-yellow-300 rounded-full" 
                             style={{boxShadow: '0 0 20px 10px rgba(255,255,0,0.5), -20px 0 30px 5px rgba(255,255,0,0.3), 20px 0 30px 5px rgba(255,255,0,0.3)'}}></div>
                        <div className="absolute top-10 right-1/4 w-4 h-4 bg-yellow-300 rounded-full"
                             style={{boxShadow: '0 0 20px 10px rgba(255,255,0,0.5), -20px 0 30px 5px rgba(255,255,0,0.3), 20px 0 30px 5px rgba(255,255,0,0.3)'}}></div>
                        <div className="absolute top-20 left-1/3 w-3 h-3 bg-white rounded-full"
                             style={{boxShadow: '0 0 15px 8px rgba(255,255,255,0.5), -15px 0 25px 4px rgba(255,255,255,0.3), 15px 0 25px 4px rgba(255,255,255,0.3)'}}></div>
                        <div className="absolute top-20 right-1/3 w-3 h-3 bg-white rounded-full"
                             style={{boxShadow: '0 0 15px 8px rgba(255,255,255,0.5), -15px 0 25px 4px rgba(255,255,255,0.3), 15px 0 25px 4px rgba(255,255,255,0.3)'}}></div>
                        <div className="absolute bottom-0 w-full h-16 bg-gray-800"></div>
                        <Moon className="absolute top-5 right-10 w-8 h-8 text-yellow-100 opacity-70" />
                        <div className="absolute bottom-16 left-0 right-0 h-1 bg-white/30"></div>
                        <p className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-white/50 text-sm">STRADA</p>
                      </div>
                    ) : (
                      // Scena diurna con effetti
                      <div className="relative w-full h-full">
                        <Sun className="absolute top-5 right-10 w-10 h-10 text-yellow-400 opacity-70" />
                        <div className="absolute bottom-16 left-0 right-0">
                          <svg viewBox="0 0 200 50" className="w-full h-16">
                            <polygon points="0,50 40,10 80,50" fill="#4a7c59" />
                            <polygon points="60,50 100,15 140,50" fill="#3d6b4a" />
                            <polygon points="120,50 160,20 200,50" fill="#4a7c59" />
                          </svg>
                        </div>
                        <div className="absolute bottom-0 w-full h-16 bg-green-500"></div>
                        <div className="absolute bottom-16 left-1/4">
                          <div className="w-16 h-16 bg-green-700 rounded-full"></div>
                          <div className="w-3 h-8 bg-amber-700 mx-auto"></div>
                        </div>
                        <p className="absolute bottom-20 right-10 text-[#2A9FCB] font-bold text-lg opacity-70">TESTO</p>
                        
                        {/* Overlay patologia specifica */}
                        {selectedSim.hasCentralSpot && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-24 h-24 bg-black/80 rounded-full" style={{
                              boxShadow: '0 0 40px 20px rgba(0,0,0,0.6)'
                            }}></div>
                          </div>
                        )}
                        
                        {selectedSim.hasFloaters && (
                          <div className="absolute inset-0">
                            <div className="absolute top-1/4 left-1/4 w-8 h-8 bg-black/40 rounded-full blur-sm"></div>
                            <div className="absolute top-1/3 right-1/3 w-6 h-6 bg-black/30 rounded-full blur-sm"></div>
                            <div className="absolute bottom-1/3 left-1/3 w-10 h-4 bg-black/35 rounded-full blur-sm transform rotate-45"></div>
                            <div className="absolute top-1/2 right-1/4 w-5 h-5 bg-black/40 rounded-full blur-sm"></div>
                            <div className="absolute bottom-1/4 right-1/2 w-7 h-3 bg-black/30 rounded-full blur-sm"></div>
                          </div>
                        )}
                        
                        {selectedSim.hasTunnelVision && (
                          <div className="absolute inset-0" style={{
                            background: 'radial-gradient(circle at center, transparent 20%, rgba(0,0,0,0.3) 35%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.95) 70%)'
                          }}></div>
                        )}
                        
                        {selectedSim.hasYellowTint && (
                          <div className="absolute inset-0 bg-yellow-900/30"></div>
                        )}
                        
                        {/* Filtri daltonismo */}
                        {selectedSim.isDaltonism === 'deuteranopia' && (
                          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{mixBlendMode: 'hue'}}>
                            <defs>
                              <filter id="deuteranopia">
                                <feColorMatrix type="matrix" values="
                                  0.625 0.375 0 0 0
                                  0.7 0.3 0 0 0
                                  0 0.3 0.7 0 0
                                  0 0 0 1 0
                                "/>
                              </filter>
                            </defs>
                            <rect width="100%" height="100%" filter="url(#deuteranopia)" fill="transparent"/>
                          </svg>
                        )}
                        {selectedSim.isDaltonism === 'protanopia' && (
                          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{mixBlendMode: 'hue'}}>
                            <defs>
                              <filter id="protanopia">
                                <feColorMatrix type="matrix" values="
                                  0.567 0.433 0 0 0
                                  0.558 0.442 0 0 0
                                  0 0.242 0.758 0 0
                                  0 0 0 1 0
                                "/>
                              </filter>
                            </defs>
                            <rect width="100%" height="100%" filter="url(#protanopia)" fill="transparent"/>
                          </svg>
                        )}
                        {selectedSim.isDaltonism === 'tritanopia' && (
                          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{mixBlendMode: 'hue'}}>
                            <defs>
                              <filter id="tritanopia">
                                <feColorMatrix type="matrix" values="
                                  0.95 0.05 0 0 0
                                  0 0.433 0.567 0 0
                                  0 0.475 0.525 0 0
                                  0 0 0 1 0
                                "/>
                              </filter>
                            </defs>
                            <rect width="100%" height="100%" filter="url(#tritanopia)" fill="transparent"/>
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Test colori per daltonismo */}
            {selectedSim.isDaltonism && (
              <div className="mt-8 p-6 bg-white rounded-xl border border-gray-200">
                <h5 className="font-semibold mb-4 text-center">Test dei Colori: Come li vede un {selectedSim.name.split('(')[1]?.replace(')', '') || 'daltonico'}</h5>
                <div className="grid grid-cols-2 gap-6">
                  {/* Visione normale */}
                  <div>
                    <p className="text-sm text-center mb-3 text-green-600 font-medium">Visione Normale</p>
                    <div className="flex justify-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-red-500" title="Rosso"></div>
                      <div className="w-12 h-12 rounded-full bg-green-500" title="Verde"></div>
                      <div className="w-12 h-12 rounded-full bg-blue-500" title="Blu"></div>
                      <div className="w-12 h-12 rounded-full bg-yellow-400" title="Giallo"></div>
                      <div className="w-12 h-12 rounded-full bg-orange-500" title="Arancione"></div>
                      <div className="w-12 h-12 rounded-full bg-purple-500" title="Viola"></div>
                    </div>
                    <div className="flex justify-center gap-3 mt-2 text-xs text-[#2A9FCB]/70">
                      <span className="w-12 text-center">Rosso</span>
                      <span className="w-12 text-center">Verde</span>
                      <span className="w-12 text-center">Blu</span>
                      <span className="w-12 text-center">Giallo</span>
                      <span className="w-12 text-center">Arancio</span>
                      <span className="w-12 text-center">Viola</span>
                    </div>
                  </div>
                  {/* Visione daltonico */}
                  <div>
                    <p className="text-sm text-center mb-3 text-red-600 font-medium">{selectedSim.name}</p>
                    <div className="flex justify-center gap-3">
                      {selectedSim.isDaltonism === 'deuteranopia' && (
                        <>
                          <div className="w-12 h-12 rounded-full" style={{background: '#9B7B4D'}} title="Rosso → Marrone"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#9B8B4D'}} title="Verde → Marrone/Giallo"></div>
                          <div className="w-12 h-12 rounded-full bg-blue-500" title="Blu (invariato)"></div>
                          <div className="w-12 h-12 rounded-full bg-yellow-400" title="Giallo (invariato)"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#9B7B4D'}} title="Arancione → Marrone"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#6B6BAD'}} title="Viola → Blu"></div>
                        </>
                      )}
                      {selectedSim.isDaltonism === 'protanopia' && (
                        <>
                          <div className="w-12 h-12 rounded-full" style={{background: '#5B5B4D'}} title="Rosso → Marrone scuro"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#8B8B4D'}} title="Verde → Giallo/Marrone"></div>
                          <div className="w-12 h-12 rounded-full bg-blue-500" title="Blu (invariato)"></div>
                          <div className="w-12 h-12 rounded-full bg-yellow-400" title="Giallo (invariato)"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#7B6B3D'}} title="Arancione → Marrone"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#5B5BAD'}} title="Viola → Blu scuro"></div>
                        </>
                      )}
                      {selectedSim.isDaltonism === 'tritanopia' && (
                        <>
                          <div className="w-12 h-12 rounded-full bg-red-500" title="Rosso (invariato)"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#4CAF9F'}} title="Verde → Verde-acqua"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#4D7B7B'}} title="Blu → Verde scuro"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#FFAAAA'}} title="Giallo → Rosa"></div>
                          <div className="w-12 h-12 rounded-full bg-orange-500" title="Arancione (invariato)"></div>
                          <div className="w-12 h-12 rounded-full" style={{background: '#8B5B5B'}} title="Viola → Marrone/Rosa"></div>
                        </>
                      )}
                    </div>
                    <div className="flex justify-center gap-3 mt-2 text-xs text-[#2A9FCB]/70">
                      {selectedSim.isDaltonism === 'deuteranopia' && (
                        <>
                          <span className="w-12 text-center">Marrone</span>
                          <span className="w-12 text-center">Marrone</span>
                          <span className="w-12 text-center">Blu</span>
                          <span className="w-12 text-center">Giallo</span>
                          <span className="w-12 text-center">Marrone</span>
                          <span className="w-12 text-center">Blu</span>
                        </>
                      )}
                      {selectedSim.isDaltonism === 'protanopia' && (
                        <>
                          <span className="w-12 text-center">Scuro</span>
                          <span className="w-12 text-center">Giallo</span>
                          <span className="w-12 text-center">Blu</span>
                          <span className="w-12 text-center">Giallo</span>
                          <span className="w-12 text-center">Marrone</span>
                          <span className="w-12 text-center">Blu</span>
                        </>
                      )}
                      {selectedSim.isDaltonism === 'tritanopia' && (
                        <>
                          <span className="w-12 text-center">Rosso</span>
                          <span className="w-12 text-center">Acqua</span>
                          <span className="w-12 text-center">Verde</span>
                          <span className="w-12 text-center">Rosa</span>
                          <span className="w-12 text-center">Arancio</span>
                          <span className="w-12 text-center">Rosa</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ==================== NUOVO: TEST ACUITÀ VISIVA BAMBINI (E di Albini) ====================
  const renderChildrenVisualAcuityTest = () => {
    const currentLine = ALBINI_E_CHILDREN[childrenVACurrentLine];
    const isLastLine = childrenVACurrentLine === ALBINI_E_CHILDREN.length - 1;
    const isLastEOfLine = childrenVACurrentE === 3; // 4 E per linea (0-3)
    
    // Ruota la E in base all'orientamento
    const getRotation = (orientation) => {
      switch(orientation) {
        case 'up': return 'rotate(-90deg)';
        case 'down': return 'rotate(90deg)';
        case 'left': return 'rotate(180deg)';
        case 'right': 
        default: return 'rotate(0deg)';
      }
    };

    const handleResponse = (response) => {
      const isCorrect = response === childrenVAOrientation;
      
      if (isCorrect) {
        setChildrenVACorrectCount(prev => prev + 1);
        toast.success('Corretto!');
        
        if (isLastEOfLine) {
          // Completate tutte le 4 E della linea
          if (isLastLine) {
            // Test completato - massima acuità raggiunta
            const result = { 
              eye: currentEye, 
              acuity: currentLine.decimi,
              linesCompleted: childrenVACurrentLine + 1 
            };
            const newResults = { ...childrenVAResults, [currentEye]: result };
            setChildrenVAResults(newResults);
            
            if (currentEye === 'right') {
              setCurrentEye('left');
              setChildrenVACurrentLine(0);
              setChildrenVACurrentE(0);
              setChildrenVACorrectCount(0);
              setChildrenVALineErrors(0);
              setChildrenVAOrientation(generateRandomOrientation());
              toast.info('Ottimo! Ora testa l\'occhio sinistro');
            } else {
              saveTestResult('Acuità Visiva Adulti e Bambini', newResults);
              toast.success(`Test completato! Acuità: ${currentLine.decimi}`);
            }
          } else {
            // Passa alla linea successiva
            setChildrenVACurrentLine(prev => prev + 1);
            setChildrenVACurrentE(0);
            setChildrenVALineErrors(0);
            setChildrenVAOrientation(generateRandomOrientation());
          }
        } else {
          // Prossima E della stessa linea
          setChildrenVACurrentE(prev => prev + 1);
          setChildrenVAOrientation(generateRandomOrientation());
        }
      } else {
        setChildrenVALineErrors(prev => prev + 1);
        toast.error('Risposta errata');
        
        // Dopo 2 errori nella stessa linea, il test per quell'occhio finisce
        if (childrenVALineErrors >= 1) {
          const result = { 
            eye: currentEye, 
            acuity: childrenVACurrentLine > 0 ? ALBINI_E_CHILDREN[childrenVACurrentLine - 1].decimi : 'Non valutabile',
            linesCompleted: childrenVACurrentLine 
          };
          const newResults = { ...childrenVAResults, [currentEye]: result };
          setChildrenVAResults(newResults);
          
          if (currentEye === 'right') {
            setCurrentEye('left');
            setChildrenVACurrentLine(0);
            setChildrenVACurrentE(0);
            setChildrenVACorrectCount(0);
            setChildrenVALineErrors(0);
            setChildrenVAOrientation(generateRandomOrientation());
            toast.info('Ora testa l\'occhio sinistro');
          } else {
            saveTestResult('Acuità Visiva Adulti e Bambini', newResults);
            toast.info('Test completato');
          }
        } else {
          // Prossima E dopo errore
          setChildrenVACurrentE(prev => prev + 1);
          if (childrenVACurrentE >= 3) {
            // Se era l'ultima E della linea, passa comunque alla prossima
            setChildrenVACurrentLine(prev => prev + 1);
            setChildrenVACurrentE(0);
            setChildrenVALineErrors(0);
          }
          setChildrenVAOrientation(generateRandomOrientation());
        }
      }
    };

    if (!isDesktop) {
      return (
        <div className="space-y-6 text-center" data-testid="children-va-test">
          <div className="p-8 rounded-xl bg-orange-50 border-2 border-orange-300">
            <Monitor className="w-16 h-16 mx-auto mb-4 text-orange-500" />
            <h3 className="text-2xl font-bold mb-4 text-orange-600">Test disponibile solo su Desktop</h3>
            <p className="text-gray-600 mb-4">
              Questo test richiede uno schermo grande per garantire le dimensioni corrette delle lettere 
              alla distanza di 3 metri.
            </p>
            <p className="text-sm text-gray-500">
              Per favore, accedi da un computer desktop o laptop per eseguire questo test.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6" data-testid="children-va-test">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2" style={{color: '#FF5722'}}>Test Acuità Visiva Adulti e Bambini</h3>
          <p className="text-[#2A9FCB]/80">Test con E di Albini - Distanza: 3 metri</p>
        </div>

        {/* Box Scarica la E */}
        <div className="p-6 rounded-xl bg-gradient-to-r from-orange-100 to-amber-50 border border-orange-200">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0 w-24 h-24 bg-white rounded-xl shadow-md flex items-center justify-center border-2 border-orange-300">
              <span className="text-6xl font-bold text-black">E</span>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h4 className="font-bold text-lg text-orange-700 mb-2">📄 Scarica e Stampa la "E"</h4>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Per i bambini:</strong> Stampa questa E e chiedi al bambino di tenerla in mano. 
                Dovrà ruotarla per metterla nella <strong>stessa posizione</strong> di quella che vede sullo schermo.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                💡 <strong>Consiglio:</strong> Questo esercizio è utile anche <strong>prima della visita oculistica</strong>! 
                Aiuta il bambino a capire come "giocare" con il medico, migliorando la collaborazione 
                e permettendo una valutazione più accurata della vista.
              </p>
              <button
                onClick={() => {
                  // Crea un canvas per generare la E da stampare
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>E di Albini - Test Acuità Visiva</title>
                        <style>
                          body { 
                            display: flex; 
                            flex-direction: column;
                            align-items: center; 
                            justify-content: center; 
                            min-height: 100vh; 
                            margin: 0;
                            font-family: Arial, sans-serif;
                          }
                          .e-container {
                            border: 3px dashed #FF5722;
                            border-radius: 20px;
                            padding: 40px;
                            margin: 20px;
                          }
                          .e-letter {
                            font-size: 200px;
                            font-weight: bold;
                            font-family: Arial, sans-serif;
                            line-height: 1;
                          }
                          .instructions {
                            max-width: 500px;
                            text-align: center;
                            margin-top: 30px;
                            padding: 20px;
                            background: #FFF7ED;
                            border-radius: 10px;
                          }
                          h1 { color: #FF5722; margin-bottom: 10px; }
                          p { color: #666; font-size: 14px; line-height: 1.6; }
                          .logo { color: #2A9FCB; font-size: 12px; margin-top: 20px; }
                          @media print {
                            .no-print { display: none; }
                          }
                        </style>
                      </head>
                      <body>
                        <div class="e-container">
                          <span class="e-letter">E</span>
                        </div>
                        <div class="instructions">
                          <h1>Come usare questa E</h1>
                          <p>
                            <strong>Ritaglia questa E</strong> lungo il bordo tratteggiato.<br><br>
                            Il bambino deve tenere la E in mano e <strong>ruotarla</strong> per metterla 
                            nella stessa posizione di quella che vede sullo schermo.<br><br>
                            Questo gioco aiuta a prepararsi per la visita oculistica! 🎮👁️
                          </p>
                          <p class="logo">PRIME VISION - Centro di Eccellenza Oftalmologica</p>
                        </div>
                        <button class="no-print" onclick="window.print()" style="margin-top: 20px; padding: 15px 30px; background: #FF5722; color: white; border: none; border-radius: 10px; font-size: 16px; cursor: pointer;">
                          🖨️ Stampa
                        </button>
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                }}
                className="px-6 py-3 rounded-xl font-semibold text-white flex items-center gap-2 hover:scale-105 transition-all"
                style={{background: '#FF5722'}}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Scarica e Stampa la E
              </button>
            </div>
          </div>
        </div>

        {/* Istruzioni */}
        <div className="p-6 rounded-xl bg-orange-50 border border-orange-200">
          <h4 className="font-semibold flex items-center gap-2 mb-4 text-orange-600">
            <Info className="w-5 h-5" />
            Istruzioni per i genitori
          </h4>
          <ol className="text-sm text-gray-700 space-y-2">
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">1</span>
              <span>Posiziona il bambino a <strong>3 metri esatti</strong> dallo schermo</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">2</span>
              <span><strong>Copri un occhio</strong> del bambino con un occlusore o la mano (senza premere)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">3</span>
              <span>Chiedi al bambino di indicare con la mano <strong>dove "guardano" le gambette della E</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">4</span>
              <span>Clicca sulla direzione o usa i <strong>tasti freccia della tastiera</strong> (↑ ↓ ← →)</span>
            </li>
          </ol>
        </div>

        {/* Selezione occhio */}
        <div className="flex justify-center gap-4">
          <button
            onClick={() => {
              setCurrentEye('right');
              setChildrenVACurrentLine(0);
              setChildrenVACurrentE(0);
              setChildrenVALineErrors(0);
              setChildrenVAOrientation(generateRandomOrientation());
            }}
            className={`px-6 py-3 rounded-xl font-medium transition-all`}
            style={{
              background: currentEye === 'right' ? '#FF5722' : '#F3F4F6',
              color: currentEye === 'right' ? '#fff' : '#2A9FCB'
            }}
          >
            👁️ Occhio DESTRO
          </button>
          <button
            onClick={() => {
              setCurrentEye('left');
              setChildrenVACurrentLine(0);
              setChildrenVACurrentE(0);
              setChildrenVALineErrors(0);
              setChildrenVAOrientation(generateRandomOrientation());
            }}
            className={`px-6 py-3 rounded-xl font-medium transition-all`}
            style={{
              background: currentEye === 'left' ? '#FF5722' : '#F3F4F6',
              color: currentEye === 'left' ? '#fff' : '#2A9FCB'
            }}
          >
            👁️ Occhio SINISTRO
          </button>
        </div>

        <p className="text-center font-semibold text-lg" style={{color: '#FF5722'}}>
          Copri l'occhio {currentEye === 'right' ? 'SINISTRO' : 'DESTRO'} del bambino
        </p>

        {/* Info linea attuale */}
        <div className="text-center space-y-1">
          <div className="text-sm text-gray-500">
            Acuità: <span className="font-bold text-lg" style={{color: '#FF5722'}}>{currentLine.decimi}</span>
          </div>
          <div className="text-xs text-gray-400">
            E numero {childrenVACurrentE + 1} di 4 | Linea {childrenVACurrentLine + 1} di {ALBINI_E_CHILDREN.length}
          </div>
          {/* Progress bar per le 4 E */}
          <div className="flex justify-center gap-1 mt-2">
            {[0, 1, 2, 3].map(i => (
              <div 
                key={i} 
                className={`w-8 h-2 rounded-full ${i < childrenVACurrentE ? 'bg-green-500' : i === childrenVACurrentE ? 'bg-orange-500' : 'bg-gray-200'}`}
              />
            ))}
          </div>
        </div>

        {/* E di Albini */}
        <div className="flex justify-center py-8">
          <div 
            className="bg-white rounded-xl shadow-lg p-8 flex items-center justify-center"
            style={{ minWidth: '300px', minHeight: '300px' }}
          >
            <span 
              className="font-bold text-black select-none"
              style={{ 
                fontSize: `${currentLine.size}px`,
                transform: getRotation(childrenVAOrientation),
                display: 'inline-block',
                fontFamily: 'Arial, sans-serif',
                lineHeight: 1
              }}
            >
              E
            </span>
          </div>
        </div>

        {/* Pulsanti risposta */}
        <div className="text-center">
          <p className="font-medium mb-2 text-[#2A9FCB]">Dove puntano le "gambette" della E?</p>
          <p className="text-xs text-gray-400 mb-4">Clicca o usa i tasti freccia ↑ ↓ ← →</p>
          <div className="grid grid-cols-3 gap-4 max-w-xs mx-auto">
            <div></div>
            <button
              onClick={() => handleResponse('up')}
              className="p-4 rounded-xl bg-orange-100 hover:bg-orange-200 transition-all focus:ring-2 focus:ring-orange-500"
              data-testid="va-btn-up"
            >
              <span className="text-3xl">⬆️</span>
              <p className="text-xs mt-1">Su (↑)</p>
            </button>
            <div></div>
            
            <button
              onClick={() => handleResponse('left')}
              className="p-4 rounded-xl bg-orange-100 hover:bg-orange-200 transition-all focus:ring-2 focus:ring-orange-500"
              data-testid="va-btn-left"
            >
              <span className="text-3xl">⬅️</span>
              <p className="text-xs mt-1">Sx (←)</p>
            </button>
            <div className="flex items-center justify-center">
              <span className="text-4xl font-bold" style={{color: '#FF5722'}}>E</span>
            </div>
            <button
              onClick={() => handleResponse('right')}
              className="p-4 rounded-xl bg-orange-100 hover:bg-orange-200 transition-all focus:ring-2 focus:ring-orange-500"
              data-testid="va-btn-right"
            >
              <span className="text-3xl">➡️</span>
              <p className="text-xs mt-1">Dx (→)</p>
            </button>
            
            <div></div>
            <button
              onClick={() => handleResponse('down')}
              className="p-4 rounded-xl bg-orange-100 hover:bg-orange-200 transition-all focus:ring-2 focus:ring-orange-500"
              data-testid="va-btn-down"
            >
              <span className="text-3xl">⬇️</span>
              <p className="text-xs mt-1">Giù (↓)</p>
            </button>
            <div></div>
          </div>
        </div>

        {/* Risultati */}
        {(childrenVAResults.right || childrenVAResults.left) && (
          <div className="mt-6 p-4 rounded-xl bg-gray-100">
            <h4 className="font-semibold mb-2">Risultati (in decimi):</h4>
            <div className="flex gap-6">
              <div>
                <span className="text-sm text-gray-500">Occhio Destro:</span>
                <span className="ml-2 font-bold text-lg" style={{color: '#FF5722'}}>
                  {childrenVAResults.right ? childrenVAResults.right.acuity : 'Non testato'}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Occhio Sinistro:</span>
                <span className="ml-2 font-bold text-lg" style={{color: '#FF5722'}}>
                  {childrenVAResults.left ? childrenVAResults.left.acuity : 'Non testato'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== TEST SENSIBILITÀ AL CONTRASTO (10 LIVELLI) ====================
  const renderContrastSensitivityTest = () => {
    const currentLevel = CONTRAST_LEVELS[contrastLevel];
    const isLastLevel = contrastLevel === CONTRAST_LEVELS.length - 1;
    
    // Genera orientamento casuale per la E
    const generateNewOrientation = () => {
      const orientations = [0, 90, 180, 270];
      const newOrientation = orientations[Math.floor(Math.random() * orientations.length)];
      setContrastOrientation(newOrientation);
    };
    
    // Inizia il test
    const startContrastTest = () => {
      setContrastTestStarted(true);
      setContrastLevel(0);
      setContrastResults([]);
      generateNewOrientation();
    };
    
    // Gestisce la risposta
    const handleContrastAnswer = (direction) => {
      // Converti l'orientamento in direzione
      const orientationToDirection = {
        0: 'right',    // E normale - punta a destra
        90: 'down',    // E ruotata 90° - punta in basso
        180: 'left',   // E ruotata 180° - punta a sinistra
        270: 'up'      // E ruotata 270° - punta in alto
      };
      
      const correctDirection = orientationToDirection[contrastOrientation];
      const isCorrect = direction === correctDirection;
      
      const newResults = [...contrastResults, {
        level: contrastLevel + 1,
        contrast: currentLevel.contrast,
        correct: isCorrect,
        userAnswer: direction,
        correctAnswer: correctDirection
      }];
      setContrastResults(newResults);
      
      if (!isLastLevel) {
        setContrastLevel(prev => prev + 1);
        generateNewOrientation();
      } else {
        // Test completato
        const correctCount = newResults.filter(r => r.correct).length;
        const lastCorrectLevel = newResults.reduce((last, r, idx) => r.correct ? idx + 1 : last, 0);
        
        completeTest('Sensibilità al Contrasto', {
          totalLevels: CONTRAST_LEVELS.length,
          correctAnswers: correctCount,
          lastCorrectLevel: lastCorrectLevel,
          results: newResults
        }, correctCount < 6, `Livello massimo raggiunto: ${lastCorrectLevel}/10`);
        
        setContrastTestStarted(false);
      }
    };
    
    // Calcola il colore della E in base al contrasto
    const getContrastColor = (contrast) => {
      const grayValue = Math.round(255 - (255 * contrast / 100));
      return `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
    };
    
    return (
      <div className="space-y-6" data-testid="contrast-sensitivity-test">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2" style={{color: '#2A9FCB'}}>Test Sensibilità al Contrasto</h3>
          <p className="text-[#2A9FCB]/80 mb-4">Valuta la capacità di percepire differenze di luminosità</p>
        </div>

        {/* Istruzioni */}
        <div className="p-4 rounded-xl mb-6" style={{background: 'rgba(42, 159, 203, 0.1)', border: '1px solid rgba(42, 159, 203, 0.3)'}}>
          <h4 className="font-semibold flex items-center gap-2 mb-2" style={{color: '#2A9FCB'}}>
            <Info className="w-5 h-5" />
            Come eseguire il test
          </h4>
          <ul className="text-sm text-[#2A9FCB]/80 space-y-1">
            <li>• Siediti a circa 50cm dallo schermo</li>
            <li>• Indica in quale direzione punta la "E"</li>
            <li>• Il contrasto diminuirà progressivamente</li>
            <li>• Se non riesci a vedere la lettera, indica comunque una direzione</li>
          </ul>
        </div>

        {!contrastTestStarted ? (
          <div className="text-center">
            <button
              onClick={startContrastTest}
              className="px-8 py-4 rounded-full text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
              style={{background: '#2A9FCB'}}
              data-testid="start-contrast-test"
            >
              Inizia Test del Contrasto
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Progress */}
            <div className="text-center">
              <p className="text-sm text-[#2A9FCB]/70 mb-2">Livello {contrastLevel + 1} di {CONTRAST_LEVELS.length}</p>
              <p className="text-xs text-gray-500">{currentLevel.label}</p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${((contrastLevel + 1) / CONTRAST_LEVELS.length) * 100}%`,
                    background: '#2A9FCB'
                  }}
                />
              </div>
            </div>

            {/* Display della E con contrasto variabile */}
            <div 
              className="flex justify-center items-center rounded-2xl mx-auto"
              style={{
                width: '300px',
                height: '300px',
                background: '#FFFFFF',
                border: '2px solid #E5E7EB'
              }}
            >
              <span 
                className="text-9xl font-bold select-none transition-all duration-300"
                style={{
                  color: getContrastColor(currentLevel.contrast),
                  transform: `rotate(${contrastOrientation}deg)`,
                  fontFamily: 'Arial, sans-serif'
                }}
                data-testid="contrast-letter"
              >
                E
              </span>
            </div>

            {/* Pulsanti direzione */}
            <div className="flex flex-col items-center gap-4">
              <button
                onClick={() => handleContrastAnswer('up')}
                className="w-16 h-16 rounded-xl bg-[#2A9FCB] text-white flex items-center justify-center hover:bg-[#1C729E] transition-all"
                data-testid="contrast-btn-up"
              >
                <ChevronRight className="w-8 h-8 transform -rotate-90" />
              </button>
              <div className="flex gap-4">
                <button
                  onClick={() => handleContrastAnswer('left')}
                  className="w-16 h-16 rounded-xl bg-[#2A9FCB] text-white flex items-center justify-center hover:bg-[#1C729E] transition-all"
                  data-testid="contrast-btn-left"
                >
                  <ChevronRight className="w-8 h-8 transform rotate-180" />
                </button>
                <button
                  onClick={() => handleContrastAnswer('right')}
                  className="w-16 h-16 rounded-xl bg-[#2A9FCB] text-white flex items-center justify-center hover:bg-[#1C729E] transition-all"
                  data-testid="contrast-btn-right"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </div>
              <button
                onClick={() => handleContrastAnswer('down')}
                className="w-16 h-16 rounded-xl bg-[#2A9FCB] text-white flex items-center justify-center hover:bg-[#1C729E] transition-all"
                data-testid="contrast-btn-down"
              >
                <ChevronRight className="w-8 h-8 transform rotate-90" />
              </button>
            </div>

            <p className="text-center text-sm text-gray-500">
              Indica in quale direzione punta la "E"
            </p>
          </div>
        )}

        {/* Interpretazione */}
        <div className="mt-6 p-4 rounded-xl" style={{background: 'rgba(42, 159, 203, 0.05)'}}>
          <h4 className="font-semibold mb-3" style={{color: '#2A9FCB'}}>Scala di interpretazione:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>Livelli 1-3:</strong> Contrasto elevato - dovrebbe essere visibile a tutti</li>
            <li>• <strong>Livelli 4-6:</strong> Contrasto medio - sensibilità normale</li>
            <li>• <strong>Livelli 7-8:</strong> Basso contrasto - buona sensibilità</li>
            <li>• <strong>Livelli 9-10:</strong> Contrasto minimo - eccellente sensibilità</li>
          </ul>
        </div>
      </div>
    );
  };

  // ==================== TEST DEQ-5 (Dry Eye Questionnaire) IN ITALIANO ====================
  const renderDEQ5Test = () => {
    const calculateDEQ5Score = () => {
      let total = 0;
      DEQ5_QUESTIONS.forEach((q) => {
        if (deq5Answers[q.id] !== undefined) {
          total += deq5Answers[q.id];
        }
      });
      return total;
    };
    
    const getDEQ5Interpretation = (score) => {
      if (score >= 12) return { level: 'Occhio secco probabile', color: '#DC2626', description: 'Il punteggio suggerisce una probabile sindrome dell\'occhio secco. Si consiglia una visita oculistica.' };
      if (score >= 6) return { level: 'Possibile occhio secco', color: '#F59E0B', description: 'Potrebbero esserci segni di occhio secco. Monitorare i sintomi e considerare una valutazione.' };
      return { level: 'Nella norma', color: '#22C55E', description: 'Il punteggio è nella norma. Continua a prenderti cura dei tuoi occhi.' };
    };
    
    const deq5Score = calculateDEQ5Score();
    const interpretation = getDEQ5Interpretation(deq5Score);
    const allAnswered = Object.keys(deq5Answers).length === DEQ5_QUESTIONS.length;
    
    return (
      <div className="space-y-6" data-testid="deq5-test">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2" style={{color: '#2A9FCB'}}>DEQ-5: Questionario Occhio Secco</h3>
          <p className="text-[#2A9FCB]/80 mb-4">Dry Eye Questionnaire - 5 domande validate</p>
        </div>

        {/* Info sul test */}
        <div className="p-4 rounded-xl mb-6" style={{background: 'rgba(42, 159, 203, 0.1)', border: '1px solid rgba(42, 159, 203, 0.3)'}}>
          <h4 className="font-semibold flex items-center gap-2 mb-2" style={{color: '#2A9FCB'}}>
            <Info className="w-5 h-5" />
            Informazioni sul DEQ-5
          </h4>
          <p className="text-sm text-[#2A9FCB]/80">
            Il DEQ-5 è un questionario validato scientificamente per lo screening della sindrome dell'occhio secco.
            Un punteggio ≥6 indica possibile occhio secco, ≥12 indica occhio secco probabile.
          </p>
        </div>

        {/* Domande */}
        <div className="space-y-6">
          {DEQ5_QUESTIONS.map((q, idx) => (
            <div 
              key={q.id} 
              className="p-5 rounded-xl border transition-all"
              style={{
                background: deq5Answers[q.id] !== undefined ? 'rgba(42, 159, 203, 0.05)' : 'white',
                borderColor: deq5Answers[q.id] !== undefined ? '#2A9FCB' : '#E5E7EB'
              }}
            >
              <p className="font-medium mb-4 text-gray-800">
                {idx + 1}. {q.question}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                {q.options.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setDeq5Answers({...deq5Answers, [q.id]: option.value})}
                    className={`px-3 py-2 rounded-lg text-sm transition-all ${
                      deq5Answers[q.id] === option.value
                        ? 'bg-[#2A9FCB] text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`deq5-q${q.id}-opt${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Risultato */}
        {allAnswered && (
          <div 
            className="p-6 rounded-xl text-center"
            style={{background: `${interpretation.color}15`, border: `2px solid ${interpretation.color}`}}
          >
            <h4 className="text-3xl font-bold mb-2" style={{color: interpretation.color}}>
              Punteggio: {deq5Score}/20
            </h4>
            <p className="text-lg font-semibold mb-2" style={{color: interpretation.color}}>
              {interpretation.level}
            </p>
            <p className="text-sm text-gray-600">{interpretation.description}</p>
          </div>
        )}

        {/* Bottone salva */}
        <div className="text-center">
          <button
            onClick={() => {
              if (allAnswered) {
                completeTest('DEQ-5', {
                  score: deq5Score,
                  maxScore: 20,
                  interpretation: interpretation.level,
                  answers: deq5Answers
                }, deq5Score >= 6, interpretation.description);
                toast.success(`Punteggio DEQ-5: ${deq5Score} - ${interpretation.level}`);
              } else {
                toast.error('Rispondi a tutte le domande prima di calcolare il punteggio');
              }
            }}
            disabled={!allAnswered}
            className={`px-8 py-3 rounded-full font-semibold transition-all ${
              allAnswered
                ? 'bg-[#2A9FCB] text-white hover:bg-[#1C729E] shadow-lg'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="deq5-submit"
          >
            Calcola Punteggio DEQ-5
          </button>
        </div>

        {/* Legenda */}
        <div className="mt-6 p-4 rounded-xl" style={{background: 'rgba(42, 159, 203, 0.05)'}}>
          <h4 className="font-semibold mb-3" style={{color: '#2A9FCB'}}>Scala di interpretazione DEQ-5:</h4>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{background: '#22C55E'}}></span>
              <strong>0-5:</strong> Nella norma - nessun segno di occhio secco
            </li>
            <li className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{background: '#F59E0B'}}></span>
              <strong>6-11:</strong> Possibile occhio secco - monitorare
            </li>
            <li className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{background: '#DC2626'}}></span>
              <strong>12-20:</strong> Occhio secco probabile - consigliata visita
            </li>
          </ul>
        </div>
      </div>
    );
  };

  // ==================== LEGACY: TEST MGD/SPEED (mantenuto per compatibilità) ====================
  const renderMGDSpeedTest = () => {
    const calculateSpeedScore = () => {
      let frequencyScore = 0;
      let severityScore = 0;
      
      SPEED_QUESTIONS[0].symptoms.forEach(s => {
        if (speedAnswers[s.id] !== undefined) frequencyScore += speedAnswers[s.id];
      });
      SPEED_QUESTIONS[1].symptoms.forEach(s => {
        if (speedAnswers[s.id] !== undefined) severityScore += speedAnswers[s.id];
      });
      
      return frequencyScore + severityScore;
    };

    const calculateMGDRisk = () => {
      let yesCount = 0;
      MGD_SPECIFIC_QUESTIONS.forEach(q => {
        if (mgdAnswers[q.id] === true) yesCount++;
      });
      return yesCount;
    };

    const speedScore = calculateSpeedScore();
    const mgdRisk = calculateMGDRisk();
    const allSpeedAnswered = Object.keys(speedAnswers).length === 8;
    const allMGDAnswered = Object.keys(mgdAnswers).length === MGD_SPECIFIC_QUESTIONS.length;

    const getSpeedInterpretation = (score) => {
      if (score <= 4) return { level: 'Normale', color: '#22C55E', description: 'Nessun segno significativo di occhio secco' };
      if (score <= 9) return { level: 'Lieve', color: '#EAB308', description: 'Sintomi lievi di occhio secco' };
      if (score <= 14) return { level: 'Moderato', color: '#F97316', description: 'Occhio secco moderato - Consigliata visita' };
      return { level: 'Severo', color: '#EF4444', description: 'Occhio secco severo - Visita urgente consigliata' };
    };

    const getMGDInterpretation = (risk) => {
      if (risk <= 1) return { level: 'Basso', color: '#22C55E', description: 'Bassa probabilità di MGD' };
      if (risk <= 2) return { level: 'Moderato', color: '#EAB308', description: 'Possibile disfunzione delle ghiandole di Meibomio' };
      return { level: 'Alto', color: '#EF4444', description: 'Alta probabilità di MGD - Consigliata valutazione specialistica' };
    };

    const speedInterpretation = getSpeedInterpretation(speedScore);
    const mgdInterpretation = getMGDInterpretation(mgdRisk);

    return (
      <div className="space-y-8" data-testid="mgd-speed-test">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2" style={{color: '#00BCD4'}}>Test Occhio Secco Evaporativo</h3>
          <p className="text-[#2A9FCB]/80">Valutazione per Disfunzione delle Ghiandole di Meibomio (MGD)</p>
        </div>

        {/* Info sulla MGD */}
        <div className="p-6 rounded-xl" style={{background: 'rgba(0, 188, 212, 0.1)', border: '1px solid rgba(0, 188, 212, 0.3)'}}>
          <h4 className="font-semibold flex items-center gap-2 mb-3" style={{color: '#00BCD4'}}>
            <Thermometer className="w-5 h-5" />
            Cos'è la MGD?
          </h4>
          <p className="text-sm text-[#2A9FCB]/80 mb-3">
            La <strong>Disfunzione delle Ghiandole di Meibomio (MGD)</strong> è la causa più comune di occhio secco evaporativo. 
            Le ghiandole di Meibomio, situate nelle palpebre, producono la componente oleosa del film lacrimale che impedisce 
            l'evaporazione delle lacrime.
          </p>
          <p className="text-sm text-[#2A9FCB]/80">
            Quando queste ghiandole non funzionano correttamente, le lacrime evaporano troppo rapidamente causando secchezza, 
            irritazione e disagio oculare.
          </p>
        </div>

        {/* PARTE 1: Questionario SPEED */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h4 className="text-lg font-bold mb-4" style={{color: '#00BCD4'}}>
            Parte 1: Questionario SPEED (Standard Patient Evaluation of Eye Dryness)
          </h4>
          
          {SPEED_QUESTIONS.map((section, sIdx) => (
            <div key={section.id} className="mb-8">
              <h5 className="font-semibold mb-2 text-[#2A9FCB]">{section.title}</h5>
              <p className="text-sm text-gray-500 mb-4">{section.description}</p>
              
              {section.symptoms.map((symptom) => (
                <div key={symptom.id} className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium mb-3 text-[#2A9FCB]">{symptom.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {section.options.map(option => (
                      <button
                        key={option.value}
                        onClick={() => setSpeedAnswers({...speedAnswers, [symptom.id]: option.value})}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          speedAnswers[symptom.id] === option.value ? 'text-white' : 'bg-white hover:bg-gray-100 border border-gray-200'
                        }`}
                        style={{
                          background: speedAnswers[symptom.id] === option.value ? '#00BCD4' : undefined
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Risultato SPEED */}
          {allSpeedAnswered && (
            <div className="p-6 rounded-xl text-center" style={{background: `${speedInterpretation.color}15`, border: `2px solid ${speedInterpretation.color}`}}>
              <div className="text-4xl font-bold mb-2" style={{color: speedInterpretation.color}}>
                {speedScore}/24
              </div>
              <div className="text-lg font-semibold mb-1" style={{color: speedInterpretation.color}}>
                {speedInterpretation.level}
              </div>
              <p className="text-[#2A9FCB]/80 text-sm">{speedInterpretation.description}</p>
            </div>
          )}
        </div>

        {/* PARTE 2: Domande specifiche MGD */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h4 className="text-lg font-bold mb-4" style={{color: '#00BCD4'}}>
            Parte 2: Valutazione Specifica MGD
          </h4>
          <p className="text-sm text-gray-500 mb-6">
            Rispondi alle seguenti domande per valutare la probabilità di disfunzione delle ghiandole di Meibomio.
          </p>

          {MGD_SPECIFIC_QUESTIONS.map((q) => (
            <div key={q.id} className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="font-medium mb-2 text-[#2A9FCB]">{q.question}</p>
              <p className="text-xs text-gray-400 mb-3 italic">{q.info}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setMgdAnswers({...mgdAnswers, [q.id]: true})}
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${
                    mgdAnswers[q.id] === true ? 'bg-cyan-500 text-white' : 'bg-white border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  Sì
                </button>
                <button
                  onClick={() => setMgdAnswers({...mgdAnswers, [q.id]: false})}
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${
                    mgdAnswers[q.id] === false ? 'bg-gray-500 text-white' : 'bg-white border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  No
                </button>
              </div>
            </div>
          ))}

          {/* Risultato MGD */}
          {allMGDAnswered && (
            <div className="p-6 rounded-xl text-center mt-6" style={{background: `${mgdInterpretation.color}15`, border: `2px solid ${mgdInterpretation.color}`}}>
              <div className="text-4xl font-bold mb-2" style={{color: mgdInterpretation.color}}>
                {mgdRisk}/5
              </div>
              <div className="text-lg font-semibold mb-1" style={{color: mgdInterpretation.color}}>
                Rischio MGD: {mgdInterpretation.level}
              </div>
              <p className="text-[#2A9FCB]/80 text-sm">{mgdInterpretation.description}</p>
            </div>
          )}
        </div>

        {/* Pulsante salva */}
        <div className="text-center">
          <button
            onClick={() => {
              if (!allSpeedAnswered || !allMGDAnswered) {
                toast.error('Rispondi a tutte le domande');
                return;
              }
              saveTestResult('MGD-SPEED', { 
                speedAnswers, 
                speedScore, 
                speedInterpretation: speedInterpretation.level,
                mgdAnswers,
                mgdRisk,
                mgdInterpretation: mgdInterpretation.level
              });
              toast.success('Test completato e salvato!');
            }}
            disabled={!allSpeedAnswered || !allMGDAnswered}
            className="px-8 py-4 rounded-xl font-semibold text-white disabled:opacity-50"
            style={{background: '#00BCD4'}}
          >
            Salva Risultati Test MGD
          </button>
        </div>

        {/* Consigli */}
        <div className="p-6 rounded-xl bg-cyan-50 border border-cyan-200">
          <h4 className="font-semibold mb-3 text-cyan-700">💡 Consigli per la MGD</h4>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• <strong>Impacchi caldi:</strong> Applica un impacco caldo sulle palpebre chiuse per 10 minuti, 1-2 volte al giorno</li>
            <li>• <strong>Igiene palpebrale:</strong> Pulisci delicatamente i bordi delle palpebre con salviettine specifiche</li>
            <li>• <strong>Omega-3:</strong> L'integrazione con omega-3 può migliorare la qualità del meibum</li>
            <li>• <strong>Ammiccamento consapevole:</strong> Durante l'uso di schermi, ricorda di ammiccare frequentemente</li>
            <li>• <strong>Visita specialistica:</strong> Un oculista può valutare le ghiandole con la meibografia e proporre trattamenti come IPL o LipiFlow</li>
          </ul>
        </div>
      </div>
    );
  };

  // ==================== NUOVO: SIMULAZIONE DALTONISMO CON IMMAGINI COMPARATIVE ====================
  const renderColorblindComparisonTest = () => {
    const colorblindTypes = [
      { id: 'deuteranopia', name: 'Deuteranopia', description: 'Deficit verde (più comune)', prevalence: '5% maschi' },
      { id: 'protanopia', name: 'Protanopia', description: 'Deficit rosso', prevalence: '1% maschi' },
      { id: 'tritanopia', name: 'Tritanopia', description: 'Deficit blu-giallo (raro)', prevalence: '0.01%' },
    ];

    // SVG filters per simulare il daltonismo
    const getColorFilter = (type) => {
      switch(type) {
        case 'deuteranopia':
          return `url(#deuteranopia-filter)`;
        case 'protanopia':
          return `url(#protanopia-filter)`;
        case 'tritanopia':
          return `url(#tritanopia-filter)`;
        default:
          return 'none';
      }
    };

    return (
      <div className="space-y-6" data-testid="colorblind-comparison-test">
        {/* SVG Filters per daltonismo - Matrici scientificamente accurate (Machado et al. 2009) */}
        <svg className="absolute" style={{width: 0, height: 0}}>
          <defs>
            {/* Deuteranopia - deficit verde (più comune) */}
            <filter id="deuteranopia-filter" colorInterpolationFilters="linearRGB">
              <feColorMatrix type="matrix" values="
                0.367 0.861 -0.228 0 0
                0.280 0.673 0.047 0 0
                -0.012 0.043 0.969 0 0
                0 0 0 1 0
              "/>
            </filter>
            {/* Protanopia - deficit rosso */}
            <filter id="protanopia-filter" colorInterpolationFilters="linearRGB">
              <feColorMatrix type="matrix" values="
                0.152 1.053 -0.205 0 0
                0.115 0.786 0.099 0 0
                -0.004 -0.048 1.052 0 0
                0 0 0 1 0
              "/>
            </filter>
            {/* Tritanopia - deficit blu (raro) */}
            <filter id="tritanopia-filter" colorInterpolationFilters="linearRGB">
              <feColorMatrix type="matrix" values="
                1.256 -0.077 -0.179 0 0
                -0.078 0.931 0.148 0 0
                0.005 0.691 0.304 0 0
                0 0 0 1 0
              "/>
            </filter>
          </defs>
        </svg>

        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2" style={{color: '#9C27B0'}}>Simulazione Daltonismo</h3>
          <p className="text-[#2A9FCB]/80">Vedi il mondo attraverso gli occhi di chi ha deficit nella percezione dei colori</p>
        </div>

        {/* Info */}
        <div className="p-4 rounded-xl" style={{background: 'rgba(156, 39, 176, 0.1)', border: '1px solid rgba(156, 39, 176, 0.3)'}}>
          <h4 className="font-semibold flex items-center gap-2 mb-2" style={{color: '#9C27B0'}}>
            <Eye className="w-5 h-5" />
            Cos'è il daltonismo?
          </h4>
          <p className="text-sm text-[#2A9FCB]/80">
            Il daltonismo è un'alterazione della percezione dei colori causata da anomalie nei coni della retina. 
            È ereditario e colpisce circa l'8% degli uomini e lo 0.5% delle donne.
          </p>
        </div>

        {/* Selezione tipo di daltonismo */}
        <div>
          <p className="font-medium mb-3 text-[#2A9FCB]">Seleziona il tipo di daltonismo da simulare:</p>
          <div className="grid md:grid-cols-3 gap-4">
            {colorblindTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setActiveColorblindType(type.id)}
                className={`p-4 rounded-xl text-left transition-all ${
                  activeColorblindType === type.id ? 'ring-2 ring-purple-500' : ''
                }`}
                style={{
                  background: activeColorblindType === type.id ? 'rgba(156, 39, 176, 0.1)' : 'white',
                  border: '1px solid #E5E7EB'
                }}
              >
                <h5 className="font-semibold text-[#2A9FCB]">{type.name}</h5>
                <p className="text-xs text-gray-500">{type.description}</p>
                <p className="text-xs text-purple-600 mt-1">Prevalenza: {type.prevalence}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Palette colori comparativa */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h4 className="font-semibold mb-4 text-center text-[#2A9FCB]">Confronto Palette Colori</h4>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Visione normale */}
            <div>
              <p className="text-center font-medium mb-4 text-green-600">👁️ Visione Normale</p>
              <div className="grid grid-cols-6 gap-2">
                <div className="aspect-square rounded-lg bg-red-500" title="Rosso"></div>
                <div className="aspect-square rounded-lg bg-orange-500" title="Arancione"></div>
                <div className="aspect-square rounded-lg bg-yellow-400" title="Giallo"></div>
                <div className="aspect-square rounded-lg bg-green-500" title="Verde"></div>
                <div className="aspect-square rounded-lg bg-blue-500" title="Blu"></div>
                <div className="aspect-square rounded-lg bg-purple-500" title="Viola"></div>
                
                <div className="aspect-square rounded-lg bg-pink-400" title="Rosa"></div>
                <div className="aspect-square rounded-lg bg-amber-600" title="Marrone"></div>
                <div className="aspect-square rounded-lg bg-lime-400" title="Lime"></div>
                <div className="aspect-square rounded-lg bg-teal-500" title="Teal"></div>
                <div className="aspect-square rounded-lg bg-indigo-500" title="Indigo"></div>
                <div className="aspect-square rounded-lg bg-gray-500" title="Grigio"></div>
              </div>
              <div className="grid grid-cols-6 gap-2 mt-1 text-xs text-gray-500 text-center">
                <span>Rosso</span>
                <span>Arancio</span>
                <span>Giallo</span>
                <span>Verde</span>
                <span>Blu</span>
                <span>Viola</span>
              </div>
            </div>

            {/* Visione daltonico */}
            <div>
              <p className="text-center font-medium mb-4 text-red-600">
                🔴 Come vede: {colorblindTypes.find(t => t.id === activeColorblindType)?.name}
              </p>
              <div className="grid grid-cols-6 gap-2" style={{filter: getColorFilter(activeColorblindType)}}>
                <div className="aspect-square rounded-lg bg-red-500"></div>
                <div className="aspect-square rounded-lg bg-orange-500"></div>
                <div className="aspect-square rounded-lg bg-yellow-400"></div>
                <div className="aspect-square rounded-lg bg-green-500"></div>
                <div className="aspect-square rounded-lg bg-blue-500"></div>
                <div className="aspect-square rounded-lg bg-purple-500"></div>
                
                <div className="aspect-square rounded-lg bg-pink-400"></div>
                <div className="aspect-square rounded-lg bg-amber-600"></div>
                <div className="aspect-square rounded-lg bg-lime-400"></div>
                <div className="aspect-square rounded-lg bg-teal-500"></div>
                <div className="aspect-square rounded-lg bg-indigo-500"></div>
                <div className="aspect-square rounded-lg bg-gray-500"></div>
              </div>
              <p className="text-xs text-gray-400 text-center mt-2">I colori appaiono diversi o confusi tra loro</p>
            </div>
          </div>
        </div>

        {/* Scena comparativa - Frutta */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h4 className="font-semibold mb-4 text-center text-[#2A9FCB]">Scena: Cesto di Frutta</h4>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Visione normale */}
            <div>
              <p className="text-center font-medium mb-3 text-green-600">Visione Normale</p>
              <div className="rounded-xl overflow-hidden bg-gradient-to-b from-amber-100 to-amber-200 p-6 h-64 relative">
                {/* Cesto */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-48 h-24 bg-amber-700 rounded-b-full"></div>
                {/* Mela rossa */}
                <div className="absolute bottom-20 left-12 w-12 h-12 bg-red-500 rounded-full shadow-md">
                  <div className="absolute top-0 left-1/2 w-1 h-3 bg-amber-800 rounded"></div>
                </div>
                {/* Mela verde */}
                <div className="absolute bottom-24 left-24 w-10 h-10 bg-green-500 rounded-full shadow-md">
                  <div className="absolute top-0 left-1/2 w-1 h-2 bg-amber-800 rounded"></div>
                </div>
                {/* Banana */}
                <div className="absolute bottom-16 right-16 w-16 h-6 bg-yellow-400 rounded-full transform rotate-12 shadow-md"></div>
                {/* Arancia */}
                <div className="absolute bottom-20 right-24 w-11 h-11 bg-orange-500 rounded-full shadow-md"></div>
                {/* Uva */}
                <div className="absolute bottom-28 left-1/2 transform -translate-x-1/2">
                  <div className="flex gap-1">
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                  </div>
                  <div className="flex gap-1 -mt-1 ml-2">
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Visione daltonico */}
            <div>
              <p className="text-center font-medium mb-3 text-red-600">
                {colorblindTypes.find(t => t.id === activeColorblindType)?.name}
              </p>
              <div className="rounded-xl overflow-hidden bg-gradient-to-b from-amber-100 to-amber-200 p-6 h-64 relative" style={{filter: getColorFilter(activeColorblindType)}}>
                {/* Cesto */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-48 h-24 bg-amber-700 rounded-b-full"></div>
                {/* Mela rossa */}
                <div className="absolute bottom-20 left-12 w-12 h-12 bg-red-500 rounded-full shadow-md">
                  <div className="absolute top-0 left-1/2 w-1 h-3 bg-amber-800 rounded"></div>
                </div>
                {/* Mela verde */}
                <div className="absolute bottom-24 left-24 w-10 h-10 bg-green-500 rounded-full shadow-md">
                  <div className="absolute top-0 left-1/2 w-1 h-2 bg-amber-800 rounded"></div>
                </div>
                {/* Banana */}
                <div className="absolute bottom-16 right-16 w-16 h-6 bg-yellow-400 rounded-full transform rotate-12 shadow-md"></div>
                {/* Arancia */}
                <div className="absolute bottom-20 right-24 w-11 h-11 bg-orange-500 rounded-full shadow-md"></div>
                {/* Uva */}
                <div className="absolute bottom-28 left-1/2 transform -translate-x-1/2">
                  <div className="flex gap-1">
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                  </div>
                  <div className="flex gap-1 -mt-1 ml-2">
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                    <div className="w-4 h-4 bg-purple-600 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 text-center mt-4">
            Nota come la mela rossa e quella verde diventano difficili da distinguere per chi ha deuteranopia o protanopia.
          </p>
        </div>

        {/* Scena comparativa - Semaforo */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h4 className="font-semibold mb-4 text-center text-[#2A9FCB]">Scena: Semaforo Stradale</h4>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Visione normale */}
            <div>
              <p className="text-center font-medium mb-3 text-green-600">Visione Normale</p>
              <div className="rounded-xl overflow-hidden bg-gradient-to-b from-sky-300 to-sky-400 p-6 h-64 relative flex items-center justify-center">
                {/* Palo */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4 h-32 bg-gray-600"></div>
                {/* Semaforo */}
                <div className="bg-gray-800 rounded-lg p-3 flex flex-col gap-2">
                  <div className="w-12 h-12 rounded-full bg-red-500 shadow-lg shadow-red-500/50"></div>
                  <div className="w-12 h-12 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50"></div>
                  <div className="w-12 h-12 rounded-full bg-green-500 shadow-lg shadow-green-500/50"></div>
                </div>
              </div>
            </div>

            {/* Visione daltonico */}
            <div>
              <p className="text-center font-medium mb-3 text-red-600">
                {colorblindTypes.find(t => t.id === activeColorblindType)?.name}
              </p>
              <div className="rounded-xl overflow-hidden bg-gradient-to-b from-sky-300 to-sky-400 p-6 h-64 relative flex items-center justify-center" style={{filter: getColorFilter(activeColorblindType)}}>
                {/* Palo */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-4 h-32 bg-gray-600"></div>
                {/* Semaforo */}
                <div className="bg-gray-800 rounded-lg p-3 flex flex-col gap-2">
                  <div className="w-12 h-12 rounded-full bg-red-500 shadow-lg shadow-red-500/50"></div>
                  <div className="w-12 h-12 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50"></div>
                  <div className="w-12 h-12 rounded-full bg-green-500 shadow-lg shadow-green-500/50"></div>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 text-center mt-4">
            Per i daltonici, i semafori possono essere difficili da interpretare. Fortunatamente, la posizione delle luci (alto=stop, basso=via) aiuta.
          </p>
        </div>

        {/* Info finale */}
        <div className="p-6 rounded-xl bg-purple-50 border border-purple-200">
          <h4 className="font-semibold mb-3 text-purple-700">ℹ️ Informazioni importanti</h4>
          <ul className="text-sm text-gray-700 space-y-2">
            <li>• Il daltonismo è quasi sempre ereditario e non può essere curato</li>
            <li>• Esistono lenti speciali (es. EnChroma) che possono migliorare la percezione dei colori</li>
            <li>• Il test di Ishihara è lo standard per la diagnosi del daltonismo</li>
            <li>• Se sospetti di essere daltonico, consulta un oculista per una valutazione completa</li>
          </ul>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================
  const renderActiveTest = () => {
    switch(activeTest) {
      case 'ishihara': return renderIshiharaTest();
      case 'amsler': return renderAmslerTest();
      case 'redgreen': return renderRedGreenTest();
      case 'osdi': return renderOSDITest();
      case 'deq5': return renderDEQ5Test();
      case 'contrast_sensitivity': return renderContrastSensitivityTest();
      case 'simulations': return renderSimulationsTest();
      case 'children_visual_acuity': return renderChildrenVisualAcuityTest();
      case 'colorblind_comparison': return renderColorblindComparisonTest();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero - Luxury */}
      <section className="pt-32 pb-16 px-6 md:px-12" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
        <div className="max-w-4xl mx-auto text-center">
          <p 
            className="text-sm font-medium tracking-[0.3em] uppercase mb-4"
            style={{ color: '#2A9FCB' }}
          >
            Autovalutazione
          </p>
          <h1 
            className="text-4xl md:text-5xl font-normal mb-6 tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
          >
            Test Visivi
          </h1>
          <p className="text-lg" style={{ color: '#666666' }}>
            Valuta la tua vista comodamente da casa con i nostri test clinici validati
          </p>
        </div>
      </section>

      {/* Test Selection */}
      {!activeTest ? (
        <section className="py-12 px-6 md:px-12">
          <div className="max-w-6xl mx-auto">
            {/* Test disponibili per tutti - Registrazione solo DOPO il test */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tests.map((test) => (
                <button
                  key={test.id}
                  onClick={() => {
                    if (test.desktopOnly && !isDesktop) {
                      toast.warning('Questo test è disponibile solo su desktop');
                    }
                    setActiveTest(test.id);
                    setCurrentEye('right');
                    setAmslerResults({ right: null, left: null });
                    setRedGreenResults({ right: null, left: null });
                    setIshiharaCurrentPlate(0);
                    setIshiharaAnswers({});
                    setOsdiAnswers({});
                    setActiveSimulation(null);
                    // Reset nuovi test
                    setChildrenVACurrentLine(0);
                    setChildrenVACurrentE(0);
                    setChildrenVAResults({ right: null, left: null });
                    setChildrenVACorrectCount(0);
                    setChildrenVALineErrors(0);
                    setChildrenVAOrientation(generateRandomOrientation());
                    setSpeedAnswers({});
                    setMgdAnswers({});
                    setActiveColorblindType('deuteranopia');
                  }}
                  className={`p-8 rounded-2xl text-left transition-all hover:shadow-xl hover:scale-[1.02] bg-white ${test.desktopOnly && !isDesktop ? 'opacity-60' : ''}`}
                  style={{border: `2px solid ${test.color}20`}}
                  data-testid={`test-card-${test.id}`}
                >
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{background: `${test.color}20`}}>
                      <test.icon className="w-7 h-7" style={{color: test.color}} />
                    </div>
                    <h3 className="text-xl font-bold mb-2" style={{color: test.color}}>{test.name}</h3>
                    <p className="text-[#2A9FCB]/80">{test.description}</p>
                    {test.desktopOnly && (
                      <p className="text-xs mt-2 text-orange-500 flex items-center gap-1">
                        <Monitor className="w-3 h-3" /> Solo desktop
                      </p>
                    )}
                  </button>
                ))}
              </div>
          </div>
        </section>
      ) : (
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => setActiveTest(null)}
              className="mb-6 flex items-center gap-2 text-[#2A9FCB]/80 hover:text-gray-900"
            >
              ← Torna ai test
            </button>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg" style={{border: '1px solid #E5E7EB'}}>
              {renderActiveTest()}
            </div>
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="p-6 rounded-2xl" style={{background: 'rgba(127, 179, 160, 0.1)', border: '1px solid rgba(127, 179, 160, 0.2)'}}>
            <h4 className="font-semibold mb-2" style={{color: '#2A9FCB'}}>⚠️ Nota Importante</h4>
            <p className="text-sm text-[#2A9FCB]/80">
              Questi test di autovalutazione hanno scopo informativo e non sostituiscono una visita oculistica professionale. 
              In caso di risultati anomali o dubbi sulla tua vista, consulta sempre un medico oculista.
            </p>
          </div>
        </div>
      </section>

      {/* Modal Post-Test */}
      <TestCompletedModal
        isOpen={showTestCompletedModal}
        onClose={() => setShowTestCompletedModal(false)}
        testResult={testCompletedResult}
        testName={testCompletedName}
        onBookVisit={handleBookVisitFromModal}
        isLoggedIn={isAuthenticated}
        onSignupSuccess={() => {
          setIsAuthenticated(true);
          window.location.reload(); // Ricarica per aggiornare lo stato
        }}
      />

      <Footer />
    </div>
  );
};

export default Tests;
